/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
    Hello there! This is a demonstration of how to override a file from the base template.

    It's necessary that the module export interface remain consistent,
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/

import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'

// original value is 25
DEFAULT_LIMIT_VALUES[0] = 3
DEFAULT_SEARCH_PARAMS.limit = 3

export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'

export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS}
export const DEFAULT_LOCALE = 'fr-CH'

export * from '@salesforce/retail-react-app/app/constants'

export const SHIPPING_COUNTRY_CODES = [
    {value: 'CA', label: 'Canada'},
    {value: 'US', label: 'United States'}
]

export const getTitleOptions = (formatMessage) => [
    {
        value: '1',
        label: formatMessage({
            id: 'addressFields.title.mr',
            defaultMessage: 'Mr.'
        })
    },
    {
        value: '2',
        label: formatMessage({
            id: 'addressFields.title.mrs',
            defaultMessage: 'Mrs.'
        })
    }
]

export const getAddressIds = (countryCodeId) => {
    const safeCountry = (countryCodeId || 'ch').toLowerCase()
    return {
        billingAddressId: `Billing_${safeCountry}`
    }
}

export const SEO_OPTIMISATION = {
    checkout: {
        title: 'Checkout'
    },
    confirmation: {
        title: 'Checkout Confirmation'
    }
}

export const THEME_COLOR = '#fff'

const adyenEn = {
    'creditCard.numberField.title': 'Card Number',
    'creditCard.expiryDateField.title': 'Expiry date',
    'creditCard.cvcField.title': 'Security code',
    'creditCard.cvcField.placeholder.3digits': '3 digits',
    'creditCard.expiryDateField.placeholder': 'MM/YY',
    'error.va.sf-cc-num.01': 'Enter a valid card number',
    'error.va.sf-cc-num.02': 'Enter the card number',
    'error.va.sf-cc-num.03': 'Enter a supported card brand',
    'error.va.sf-cc-num.04': 'Enter the complete card number',
    'error.va.sf-cc-dat.01': 'Enter a valid expiry date',
    'error.va.sf-cc-dat.02': 'Enter a valid expiry date',
    'error.va.sf-cc-dat.03': 'Credit card about to expire',
    'error.va.sf-cc-dat.04': 'Enter the expiry date',
    'error.va.sf-cc-dat.05': 'Enter the complete expiry date',
    'error.va.sf-cc-mth.01': 'Enter the expiry month',
    'error.va.sf-cc-yr.01': 'Enter the expiry year',
    'error.va.sf-cc-yr.02': 'Enter the complete expiry year',
    'error.va.sf-cc-cvc.01': 'Enter the security code',
    'error.va.sf-cc-cvc.02': 'Enter the complete security code',
    payButton: 'Pay',
    'payButton.redirecting': 'Redirecting...',
    'payButton.with': 'Pay %{value} with %{maskedData}',
    'payButton.saveDetails': 'Save details',
    close: 'Close',
    storeDetails: 'Save for my next payment',
    readMore: 'Read more',
    'creditCard.holderName': 'Name on card',
    'creditCard.holderName.invalid': 'Enter name as shown on card',
    'creditCard.expiryDateField.month': 'Month',
    'creditCard.expiryDateField.year': 'Year',
    'creditCard.storeDetailsButton': 'Remember for next time',
    'creditCard.dualBrand.title': 'Card Brand',
    'creditCard.dualBrand.description':
        'Select the card brand you prefer to pay with. This is optional.',
    installments: 'Number of installments',
    installmentOption: '%{times}x %{partialValue}',
    installmentOptionMonths: '%{times} months',
    'installments.oneTime': 'One time payment',
    'installments.installments': 'Installments payment',
    'installments.revolving': 'Revolving payment',
    'sepaDirectDebit.ibanField.invalid': 'Invalid account number',
    'sepa.ownerName': 'Holder Name',
    'sepa.ibanNumber': 'Account Number (IBAN)',
    'error.title': 'Error',
    'error.subtitle.redirect': 'Redirect failed',
    'error.subtitle.payment': 'Payment failed',
    'error.subtitle.refused': 'Payment refused',
    'error.message.unknown': 'An unknown error occurred',
    'errorPanel.title': 'Existing errors',
    'idealIssuer.selectField.title': 'Bank',
    'creditCard.success': 'Payment Successful',
    loading: 'Loading…',
    continue: 'Continue',
    continueTo: 'Continue to',
    'wechatpay.timetopay': 'You have %@ to pay',
    'sr.wechatpay.timetopay': 'You have %#minutes%# %#seconds%# to pay',
    'wechatpay.scanqrcode': 'Scan QR code',
    personalDetails: 'Personal details',
    companyDetails: 'Company details',
    'companyDetails.name': 'Company name',
    'companyDetails.registrationNumber': 'Registration number',
    socialSecurityNumber: 'Social security number',
    firstName: 'First name',
    'firstName.invalid': 'Enter your first name',
    infix: 'Prefix',
    lastName: 'Last name',
    'lastName.invalid': 'Enter your last name',
    mobileNumber: 'Mobile number',
    'mobileNumber.invalid': 'Invalid mobile number',
    city: 'City',
    postalCode: 'Postal code',
    'postalCode.optional': 'Postal code (optional)',
    countryCode: 'Country Code',
    telephoneNumber: 'Telephone number',
    telephonePrefix: 'Prefix',
    dateOfBirth: 'Date of birth',
    shopperEmail: 'Email address',
    gender: 'Gender',
    'gender.notselected': 'Select your gender',
    male: 'Male',
    female: 'Female',
    billingAddress: 'Billing address',
    street: 'Street',
    stateOrProvince: 'State or province',
    country: 'Country/Region',
    houseNumberOrName: 'House number',
    separateDeliveryAddress: 'Specify a separate delivery address',
    deliveryAddress: 'Delivery Address',
    'deliveryAddress.firstName': 'Recipient first name',
    'deliveryAddress.lastName': 'Recipient last name',
    zipCode: 'Zip code',
    apartmentSuite: 'Apartment / Suite',
    provinceOrTerritory: 'Province or Territory',
    cityTown: 'City / Town',
    address: 'Address',
    'address.search.contextualText': 'Type to start searching your address',
    'address.errors.incomplete': 'Enter an address to continue',
    'address.enterManually': 'Enter address manually',
    state: 'State',
    'field.title.optional': '(optional)',
    'issuerList.selectField.contextualText': 'Select your bank to continue',
    'issuerList.selectField.label': 'Bank selection',
    privacyPolicy: 'Privacy policy',
    'afterPay.agreement': 'I agree with the %@ of Riverty',
    'riverty.termsAndConditions':
        'I agree with the general %#Terms & Conditions%# for the Riverty Payment Method. The privacy policy of Riverty can be found %#here%#.',
    paymentConditions: 'payment conditions',
    openApp: 'Open the app',
    'voucher.readInstructions': 'Read instructions',
    'voucher.introduction':
        'Thank you for your purchase, please use the following coupon to complete your payment.',
    'voucher.expirationDate': 'Expiration Date',
    'voucher.alternativeReference': 'Alternative Reference',
    'voucher.paymentReferenceLabel': 'Payment Reference',
    'voucher.surcharge': 'Incl. %@ surcharge',
    'voucher.introduction.doku':
        'Thank you for your purchase, please use the following information to complete your payment.',
    'voucher.shopperName': 'Shopper Name',
    'voucher.merchantName': 'Merchant',
    'voucher.introduction.econtext':
        'Thank you for your purchase, please use the following information to complete your payment.',
    'voucher.telephoneNumber': 'Phone Number',
    'voucher.shopperReference': 'Shopper Reference',
    'voucher.collectionInstitutionNumber': 'Collection Institution Number',
    'voucher.econtext.telephoneNumber.invalid': 'Telephone number must be 10 or 11 digits long',
    'boletobancario.btnLabel': 'Generate Boleto',
    'boleto.sendCopyToEmail': 'Send a copy to my email',
    'button.copy': 'Copy',
    'button.copied': 'Copied!',
    'button.download': 'Download',
    'boleto.socialSecurityNumber': 'CPF/CNPJ',
    'boleto.socialSecurityNumber.invalid': 'Enter a valid CPF/CNPJ number',
    'creditCard.storedCard.description.ariaLabel': 'Stored card ends in %@',
    'voucher.entity': 'Entity',
    donateButton: 'Donate',
    donationTermsCondition: 'By donating you agree to the %#terms and conditions%#',
    donationRoundUpDescription:
        '%{donationAmount} is a round up from your original payment (%{originalPaymentAmount})',
    notNowButton: 'Not now',
    thanksForYourSupport: 'Thanks for your support!',
    'resultMessages.preauthorized': 'Details saved',
    preauthorizeWith: 'Preauthorize with',
    confirmPreauthorization: 'Confirm preauthorization',
    confirmPurchase: 'Confirm purchase',
    applyGiftcard: 'Redeem',
    giftcardBalance: 'Gift card balance',
    deductedBalance: 'Deducted balance',
    'creditCard.taxNumber': 'Cardholder birthdate or Corporate registration number',
    'creditCard.taxNumber.label':
        'Cardholder birthdate (YYMMDD) or Corporate registration number (10 digits)',
    'creditCard.taxNumber.labelAlt': 'Corporate registration number (10 digits)',
    'creditCard.taxNumber.invalid': 'Invalid Cardholder birthdate or Corporate registration number',
    'storedPaymentMethod.disable.button': 'Remove',
    'storedPaymentMethod.disable.confirmation': 'Remove stored payment method',
    'storedPaymentMethod.disable.confirmButton': 'Yes, remove',
    'storedPaymentMethod.disable.cancelButton': 'Cancel',
    'ach.bankAccount': 'Bank account',
    'ach.bankAccount.title': 'Bank account details',
    'ach.bankAccount.label': 'Account type',
    'ach.bankAccount.placeholder': 'Choose an account type',
    'ach.bankAccount.nothing-selected-error': 'Choose an account type',
    'ach.bankAccount.option.personal-checking': 'Personal Checking Account',
    'ach.bankAccount.option.personal-savings': 'Personal Savings Account',
    'ach.bankAccount.option.business-checking': 'Business Checking Account',
    'ach.bankAccount.option.business-savings': 'Business Savings Account',
    'ach.bankAccountNumberVerification.label': 'Verify account number',
    'ach.bankAccountNumberVerification.error.not-match': 'Account number does not match',
    'ach.bankAccountNumberVerification.error.empty':
        'Enter the provided account number to verify the account number',
    'ach.routingNumber.label': 'Routing number',
    'ach.accountHolderNameField.title': 'Account holder name',
    'ach.accountHolderNameField.contextualText': 'Located on the front of your card',
    'ach.accountHolderNameField.invalid': 'Invalid account holder name',
    'ach.accountNumberField.invalid': 'Invalid account number',
    'ach.accountLocationField.invalid': 'Invalid ABA routing number',
    'ach.savedBankAccount': 'Saved bank account',
    'ach.savings': 'Savings account',
    'ach.checking': 'Checking account',
    'select.state': 'Select state',
    'select.stateOrProvince': 'Select state or province',
    'select.provinceOrTerritory': 'Select province or territory',
    'select.country': 'Select country/region',
    'select.noOptionsFound': 'No options found',
    'telephoneNumber.invalid': 'Invalid telephone number',
    qrCodeOrApp: 'or',
    'paypal.processingPayment': 'Processing payment...',
    generateQRCode: 'Generate QR code',
    'await.waitForConfirmation': 'Waiting for confirmation',
    'mbway.confirmPayment': 'Confirm your payment on the MB WAY app',
    'shopperEmail.invalid': 'Invalid email address',
    'dateOfBirth.format': 'DD/MM/YYYY',
    'dateOfBirth.invalid':
        'Enter a valid date of birth that indicates you are at least 18 years old',
    'blik.confirmPayment': 'Open your banking app to confirm the payment.',
    'blik.invalid': 'Enter 6 numbers',
    'blik.code': '6-digit code',
    'blik.help': 'Get the code from your banking app.',
    'swish.pendingMessage':
        'After you scan, the status can be pending for up to 10 minutes. Attempting to pay again within this time may result in multiple charges.',
    'field.valid': 'Field valid',
    'field.invalid': 'Field not valid',
    'field.error.required': 'Enter the %{label}',
    'field.error.invalid': 'Enter a valid %{label}',
    'error.giftcard.no-balance': 'This gift card has zero balance',
    'error.giftcard.card-error': 'In our records we have no gift card with this number',
    'error.giftcard.currency-error':
        'Gift cards are only valid in the currency they were issued in',
    'err.gen.9100': 'Incomplete field',
    'err.gen.9101': 'Field not valid',
    'err.gen.9102': 'Data parsing error',
    '3ds.chal.805': 'Challenge resolved without a %{result} object',
    'cc.num.902': 'Enter a valid card number',
    'cc.num.900': 'Enter the card number',
    'cc.num.903': 'Enter a supported card brand',
    'cc.num.901': 'Enter the complete card number',
    'cc.dat.912': 'Enter a valid expiry date',
    'cc.dat.913': 'Enter a valid expiry date',
    'cc.dat.914': 'Credit card about to expire',
    'cc.dat.910': 'Enter the expiry date',
    'cc.dat.911': 'Enter the complete expiry date',
    'cc.mth.915': 'Enter the expiry month',
    'cc.yr.917': 'Enter the expiry year',
    'cc.yr.918': 'Enter the complete expiry year',
    'cc.cvc.920': 'Enter the security code',
    'cc.cvc.921': 'Enter the complete security code',
    'ach.num.945': 'Enter the bank account number',
    'ach.num.946': 'Enter the complete bank account number',
    'ach.loc.947': 'Enter the bank routing number',
    'ach.loc.948': 'Enter the complete bank routing number',
    'kcp.pwd.940': 'Enter the password',
    'kcp.pwd.941': 'Enter the complete password',
    'amazonpay.signout': 'Sign out from Amazon',
    'amazonpay.changePaymentDetails': 'Change payment details',
    'partialPayment.warning': 'Select another payment method to pay the remaining',
    'partialPayment.remainingBalance': 'Remaining balance will be %{amount}',
    'bankTransfer.beneficiary': 'Beneficiary name',
    'bankTransfer.iban': 'IBAN',
    'bankTransfer.bic': 'BIC',
    'bankTransfer.reference': 'Reference',
    'bankTransfer.accountNumber': 'Account number',
    'bankTransfer.sortCode': 'Sort code',
    'bankTransfer.routingNumber': 'Routing number',
    'bankTransfer.branchCode': 'Branch code',
    'bankTransfer.bankCode': 'Bank code',
    'bankTransfer.introduction':
        'Press ‘Continue’ to create a new bank transfer payment. You can use the details in the following screen to finalize this payment.',
    'bankTransfer.instructions':
        'Thank you for your purchase, please use the following information to complete your payment.',
    'bankTransfer.shopperEmail.contextualText':
        'A copy of the payment details will be sent to your email so you can complete the payment later.',
    'bankTransfer.details.title': 'Payment details',
    'bankTransfer.instruction.title': 'Payment process',
    'bankTransfer.instruction.step1': 'Open your banking app',
    'bankTransfer.instruction.step2': 'Start a new transfer',
    'bankTransfer.instruction.step3': 'Enter the account details provided below',
    'bankTransfer.instruction.step4': 'Review and confirm the payment',
    'bacs.accountHolderName': 'Bank account holder name',
    'bacs.accountHolderName.invalid': 'Invalid bank account holder name',
    'bacs.accountNumber': 'Bank account number',
    'bacs.accountNumber.invalid': 'Invalid bank account number',
    'bacs.bankLocationId': 'Sort code',
    'bacs.bankLocationId.invalid': 'Invalid sort code',
    'bacs.consent.amount': 'I agree that the above amount will be deducted from my bank account.',
    'bacs.consent.account':
        'I confirm the account is in my name and I am the only signatory required to authorise the Direct Debit on this account.',
    edit: 'Edit',
    'bacs.confirm': 'Confirm and pay',
    'bacs.result.introduction': 'Download your Direct Debit Instruction (DDI / Mandate)',
    'download.pdf': 'Download PDF',
    'creditCard.encryptedCardNumber.aria.iframeTitle': 'Iframe for card number',
    'creditCard.encryptedExpiryDate.aria.iframeTitle': 'Iframe for expiry date',
    'creditCard.encryptedExpiryMonth.aria.iframeTitle': 'Iframe for expiry month',
    'creditCard.encryptedExpiryYear.aria.iframeTitle': 'Iframe for expiry year',
    'creditCard.encryptedSecurityCode.aria.iframeTitle': 'Iframe for security code',
    'creditCard.encryptedPassword.aria.iframeTitle': 'Iframe for password',
    'giftcard.encryptedCardNumber.aria.iframeTitle': 'Iframe for card number',
    'giftcard.encryptedSecurityCode.aria.iframeTitle': 'Iframe for pin',
    giftcardTransactionLimit: 'Max. %{amount} allowed per transaction on this gift card',
    'ach.encryptedBankAccountNumber.aria.iframeTitle': 'Iframe for bank account number',
    'ach.encryptedBankLocationId.aria.iframeTitle': 'Iframe for bank routing number',
    'pix.instructions':
        'Use your banking app to scan the QR code or copy the PIX code below to complete your payment.',
    'pix.code.label': 'PIX code',
    'pix.code.copy.label': 'Copy PIX code',
    'pix.code.copied.label': 'PIX code copied',
    'twint.saved': 'saved',
    orPayWith: 'or pay with',
    'invalid.format.expects': '%{label} Invalid format. Expected format: %{format}',
    'upi.qrCodeWaitingMessage':
        'Scan the QR code using your preferred UPI app to complete the payment',
    'upi.vpaWaitingMessage': 'Open your UPI app to confirm the payment',
    'upi.modeSelection': 'How would you like to use UPI?',
    'upi.completePayment': 'Complete your payment',
    'upi.mode.enterUpiId': 'UPI ID',
    'upi.mode.qrCode': 'QR code',
    'upi.mode.payByAnyUpi': 'UPI app',
    'upi.collect.dropdown.label': 'Enter UPI ID',
    'upi.collect.instruction': 'Enter your UPI ID',
    'upi.collect.field.label': 'UPI ID',
    'upi.collect.field.contextualText':
        'Enter the UPI ID connected to your account and press continue',
    'upi.collect.field.invalid-id-error': 'Enter a valid UPI',
    'upi.error.noAppSelected': 'Select your preferred UPI app to continue',
    'upi.intent.instruction': 'Select your preferred UPI app',
    'upi.qrCode.instruction':
        'After generating the QR code you can use your preferred UPI app to complete the payment.',
    'onlineBanking.termsAndConditions': 'By continuing you agree with the %#terms and conditions%#',
    'onlineBankingPL.termsAndConditions':
        'By continuing you agree with the %#regulations%# and %#information obligation%# of Przelewy24',
    'ctp.loading.poweredByCtp': 'Powered by Click to Pay',
    'ctp.loading.intro': 'We are checking to see if you have any saved cards with Click to Pay...',
    'ctp.login.title': 'Continue to Click to Pay',
    'ctp.login.subtitle': 'Enter the email address that is connected to Click to Pay to continue.',
    'ctp.login.inputLabel': 'Email',
    'ctp.logout.notYou': 'Not you?',
    'ctp.logout.notYourCards': 'Not your cards?',
    'ctp.logout.notYourCard': 'Not your card?',
    'ctp.logout.notYourProfile': 'Not your profile?',
    'ctp.otp.fieldLabel': 'One time code',
    'ctp.otp.resendCode': 'Resend code',
    'ctp.otp.codeResent': 'Code resent',
    'ctp.otp.title': 'Access your Click to Pay cards',
    'ctp.otp.subtitle': 'Enter the code %@ sent to %@ to verify it‘s you.',
    'ctp.otp.saveCookiesCheckbox.label': 'Skip verification next time',
    'ctp.otp.saveCookiesCheckbox.information':
        'Select to be remembered on your device and browser at participating stores for faster checkout. Not recommended for shared devices.',
    'ctp.otp.saveCookiesCheckbox.shorterInfo': 'Select to be remembered on your device and browser',
    'ctp.emptyProfile.message': 'No cards registered in this Click to Pay profile',
    'ctp.separatorText': 'or use',
    'ctp.cards.title': 'Complete payment with Click to Pay',
    'ctp.cards.subtitle': 'Select a card to use.',
    'ctp.cards.expiredCard': 'Expired',
    'ctp.manualCardEntry': 'Manual card entry',
    'ctp.aria.infoModalButton': 'What is Click to Pay',
    'ctp.infoPopup.title': 'Click to Pay brings the ease of contactless, online',
    'ctp.infoPopup.subtitle':
        'A fast, secure payment method supported by Mastercard, Visa and other payment cards.',
    'ctp.infoPopup.benefit1':
        'Click to Pay uses encryption to keep your information safe and secure',
    'ctp.infoPopup.benefit2': 'Use it with merchants worldwide',
    'ctp.infoPopup.benefit3': 'Set up once for hassle-free payments in the future',
    'ctp.errors.AUTH_INVALID': 'Authentication Invalid',
    'ctp.errors.NOT_FOUND':
        'No account found, enter a valid email or continue using manual card entry',
    'ctp.errors.ID_FORMAT_UNSUPPORTED': 'Format not supported',
    'ctp.errors.FRAUD': 'The user account was locked or disabled',
    'ctp.errors.CONSUMER_ID_MISSING': 'Consumer identity is missing in the request',
    'ctp.errors.ACCT_INACCESSIBLE': 'This account is currently not available, e.g it is locked',
    'ctp.errors.CODE_INVALID': 'Incorrect verification code',
    'ctp.errors.CODE_EXPIRED': 'This code has expired',
    'ctp.errors.RETRIES_EXCEEDED':
        'The limit for the number of retries for OTP generation was exceeded',
    'ctp.errors.OTP_SEND_FAILED': 'The OTP could not be sent to the recipient',
    'ctp.errors.REQUEST_TIMEOUT': 'Something went wrong, try again or use the manual card entry',
    'ctp.errors.UNKNOWN_ERROR': 'Something went wrong, try again or use the manual card entry',
    'ctp.errors.SERVICE_ERROR': 'Something went wrong, try again or use the manual card entry',
    'ctp.errors.SERVER_ERROR': 'Something went wrong, try again or use the manual card entry',
    'ctp.errors.INVALID_PARAMETER': 'Something went wrong, try again or use the manual card entry',
    'ctp.errors.AUTH_ERROR': 'Something went wrong, try again or use the manual card entry',
    'paymentMethodsList.aria.label': 'Choose a payment method',
    'paymentMethodsList.storedPayments.label': 'Saved payment methods',
    'paymentMethodsList.otherPayments.label': 'More payment options',
    'paymentMethodsList.otherPayments.buttonLabel': 'Show %{count} more',
    'paymentMethodsList.promotedPayments.label': 'Other',
    'companyDetails.name.invalid': 'Enter the company name',
    'companyDetails.registrationNumber.invalid': 'Enter the registration number',
    'consent.checkbox.invalid': 'You must agree with the terms & conditions',
    'form.instruction': 'All fields are required unless marked otherwise.',
    'trustly.descriptor': 'Instant Bank Payment',
    'trustly.description1':
        'Pay directly from any of your bank accounts, backed by bank-level security',
    'trustly.description2': 'No cards, no app download, no registration',
    'ancv.input.label': 'Your ANCV identification',
    'ancv.confirmPayment': 'Use your ANCV application to confirm the payment.',
    'ancv.form.instruction':
        'The Cheque-Vacances application is necessary to validate this payment.',
    'ancv.beneficiaryId.invalid': 'Enter a valid email address or ANCV ID',
    'creditCard.cardNumber.label': 'Card number',
    'creditCard.expiryDate.label': 'Expiry date',
    'creditCard.expiryDate.contextualText': 'Front of card in MM/YY format',
    'creditCard.securityCode.label': 'Security code',
    'creditCard.securityCode.label.optional': 'Security code (optional)',
    'creditCard.securityCode.contextualText.3digits': '3 digits on back of card',
    'creditCard.securityCode.contextualText.4digits': '4 digits on front of card',
    'creditCard.expiryMonth.label': 'Expiry month',
    'creditCard.expiryYear.label': 'Expiry year',
    'creditCard.pin.label': 'Pin',
    'creditCard.password.label': 'First 2 digits of card password',
    'giftcard.cardNumber.label': 'Card Number',
    'giftcard.expiryDate.label': 'Expiry date',
    'giftcard.securityCode.label': 'Pin',
    'ach.bankAccountNumber.label': 'Account number',
    'ach.bankLocationId.label': 'ABA routing number',
    'dragonpayVoucher.selectField.contextualText.nonBank': 'Select your provider',
    'dragonpayVoucher.selectField.contextualText.bank': 'Select your bank',
    'payme.openPayMeApp':
        'Complete your payment in the PayMe app by authorizing the payment in the app and wait for the confirmation.',
    'payme.redirectButtonLabel': 'Open PayMe app',
    'payme.scanQrCode': 'Complete your payment by QR code',
    'payme.timeToPay': 'This QR code is valid for %@',
    'payme.instructions.steps':
        'Open the PayMe app.%@Scan the QR code to authorize the payment.%@Complete the payment in the app and wait for confirmation.',
    'payme.instructions.footnote': 'Please do not close this page before the payment is completed',
    'payByBankAISDD.disclaimer.header': 'Use Pay by Bank to pay instantly from any bank account.',
    'payByBankAISDD.disclaimer.body':
        'By connecting your bank account you are authorizing debits to your account for any amount owed for use of our services and/or purchase of our products, until this authorization is revoked.',
    'paymentMethodBrand.other': 'other',
    'paynow.scanQrCode': 'Scan the QR code using the PayNow app to complete the payment',
    'paynow.mobileViewInstruction.step1': 'Take a screenshot of the QR code.',
    'paynow.mobileViewInstruction.step2': 'Open the PayNow bank or payment app.',
    'paynow.mobileViewInstruction.step3': 'Select the option to scan a QR code.',
    'paynow.mobileViewInstruction.step4':
        'Choose the option to upload a QR and select the screenshot.',
    'paynow.mobileViewInstruction.step5': 'Complete the transaction.',
    'payto.payid.description':
        'Enter the PayID and account details that are connected to your PayTo account.',
    'payto.payid.label.identifier': 'Identifier',
    'payto.payid.label.orgid': 'Organization ID',
    'payto.payid.option.phone': 'Mobile',
    'payto.payid.option.email': 'Email',
    'payto.payid.option.orgid': 'Organization ID',
    'payto.bsb.description':
        'Enter the bank account number and the Bank State Branch that is connected to your account to continue',
    'payto.bsb.label.bankAccountNumber': 'Bank account number',
    'payto.instructions.steps':
        'Log into your PayTo banking app@Authorize the PayTo agreement@Approve the payment terms@Complete the transaction',
    'payto.confirmPayment':
        'Thank you for your purchase, complete your payment by following the instructions below.',
    'payto.mandate.amount.label': 'Amount',
    'payto.mandate.frequency.label': 'Frequency',
    'payto.mandate.remarks.label': 'Description',
    'payto.mandate.startsAt.label': 'First payment date',
    'payto.mandate.endsAt.label': 'End date',
    'payto.mandate.payee.label': 'Payee',
    'payto.mandate.amount.max': 'Up to %{amount} per transaction',
    'payto.mandate.frequency.adhoc-no-count': 'Ad Hoc',
    'payto.mandate.frequency.adhoc': '%{count} time(s)',
    'payto.mandate.frequency.daily': '%{count} payment(s) Daily',
    'payto.mandate.frequency.weekly': '%{count} payment(s) Weekly',
    'payto.mandate.frequency.biWeekly': '%{count} payment(s) Fortnightly',
    'payto.mandate.frequency.monthly': '%{count} payment(s) Monthly',
    'payto.mandate.frequency.quarterly': '%{count} payment(s) Quarterly',
    'payto.mandate.frequency.halfYearly': '%{count} payment(s) Bi-Annual',
    'payto.mandate.frequency.yearly': '%{count} payment(s) Yearly',
    'payto.label.firstName': 'Account holder first name',
    'payto.label.lastName': 'Account holder last name',
    'payto.await.waitForConfirmation': 'Awaiting your approval',
    'payto.bsb.option.label': 'BSB and account number',
    'card.fastlane.a11y.logo': 'Fastlane by Paypal logo',
    'card.fastlane.a11y.openDialog': 'Read more about Fastlane',
    'card.fastlane.a11y.closeDialog': 'Close dialog',
    'card.fastlane.mobileInputLabel': 'Mobile number',
    'card.fastlane.consentToggle': 'Save your info with Fastlane for faster checkouts and more',
    'card.fastlane.consentText':
        "By saving your info, you agree to get codes by text to use Fastlane everywhere it's available. Fastlane will text the number provided. You also agree to this %#terms%# and %#privacy statement%#.",
    'card.fastlane.modal.benefit1.header': 'Autofill your checkouts',
    'card.fastlane.modal.benefit1.text':
        'Get one-time codes to use card and addresses you’ve saved.',
    'card.fastlane.modal.benefit2.header': 'Protect your info',
    'card.fastlane.modal.benefit2.text':
        'Your payment info is encrypted when it’s stored and sent to places you shop.',
    'card.fastlane.modal.benefit3.header': 'Use across stores',
    'card.fastlane.modal.benefit3.text': 'Speed through checkout everywhere Fastlane is available.',
    'paybybankpix.redirectBtn.label': 'Continue to PIX through OpenBanking',
    'paybybankpix.issuerList.disclaimer':
        'By continuing, you agree to the %#Consent Notice%# and will be redirected to your bank.',
    'paybybankpix.issuerList.selectField.label': 'Bank',
    'paybybankpix.issuerList.introduction.logo.content':
        'Connect your favorite PIX keys to pay through Open Finance',
    'paybybankpix.await.waitForConfirmation': 'Waiting for your confirmation...',
    'paybybankpix.await.timeToPay': 'This process will take approximately %{numberOfMin} minute(s)',
    'paybybankpix.await.withOpenFinance': 'With the ease of Open Finance',
    'paybybankpix.await.logoAlt.openFinance': 'Open Finance',
    'paybybankpix.storedPayment.payButton.label': 'Continue payment',
    'paybybankpix.storedPayment.additionalLabel': '%{receiver} account through PIX',
    'paybybankpix.await.fetchDetails': 'Fetching details...',
    'eftpad-canada.input.accountHolderName.label': 'Account holder name',
    'eftpad-canada.input.accountHolderName.error': 'Enter an account holder name',
    'eftpad-canada.input.accountNumber.label': 'Account number',
    'eftpad-canada.input.accountNumber.contextualText': 'Between 7 and 12 digits',
    'eftpad-canada.input.accountNumber.error':
        'Enter an account number that is between 7 and 12 digits',
    'eftpad-canada.input.institutionNumber.label': 'Institution number',
    'eftpad-canada.input.institutionNumber.contextualText': '3 digits',
    'eftpad-canada.input.institutionNumber.error':
        'Enter an institution number that consists of 3 digits',
    'eftpad-canada.input.transitNumber.label': 'Transit number',
    'eftpad-canada.input.transitNumber.contextualText': '5 digits',
    'eftpad-canada.input.transitNumber.error': 'Enter a transit number that consists of 5 digits',
    'eftpad-canada.settlement-info':
        'The funds will be withdrawn from your bank account within 1–3 business days.',
    'upi.mandate.intro': 'You’re setting up a UPI Autopay recurring payment',
    'upi.mandate.max.extraText': 'You’ll approve a higher limit to allow future plan changes',
    'upi.mandate.upTo': 'up to %{amount}',
    'upi.mandate.frequency.monthly': '/month',
    'upi.mandate.frequency.weekly': '/week',
    'upi.mandate.frequency.adhoc': ' as presented'
}
const adyenFr = {
    'creditCard.numberField.title': 'Numéro de la carte',
    'creditCard.expiryDateField.title': "Date d'expiration",
    'creditCard.cvcField.title': 'Code de sécurité',
    'creditCard.cvcField.placeholder.3digits': '3 chiffres',
    'creditCard.expiryDateField.placeholder': 'MM/AA',
    'error.va.sf-cc-num.01': 'Entrez un numéro de carte valide',
    'error.va.sf-cc-num.02': 'Entrez le numéro de la carte',
    'error.va.sf-cc-num.03': 'Entrez un émetteur de carte pris en charge',
    'error.va.sf-cc-num.04': 'Entrez le numéro de carte complet',
    'error.va.sf-cc-dat.01': "Entrez une date d'expiration valide",
    'error.va.sf-cc-dat.02': "Entrez une date d'expiration valide",
    'error.va.sf-cc-dat.03': 'La carte de crédit arrive à expiration',
    'error.va.sf-cc-dat.04': "Entrez la date d'expiration",
    'error.va.sf-cc-dat.05': "Entrez la date d'expiration complète",
    'error.va.sf-cc-mth.01': "Entrez le mois d'expiration",
    'error.va.sf-cc-yr.01': "Entrez l'année d'expiration",
    'error.va.sf-cc-yr.02': "Entrez l'année d'expiration complète",
    'error.va.sf-cc-cvc.01': 'Entrez le code de sécurité',
    'error.va.sf-cc-cvc.02': 'Entrez le code de sécurité complet',
    payButton: 'Payer',
    'payButton.redirecting': 'Redirection...',
    'payButton.with': 'Payer %{value} avec %{maskedData}',
    'payButton.saveDetails': 'Enregistrer les détails',
    close: 'Fermer',
    storeDetails: 'Sauvegarder pour mon prochain paiement',
    readMore: 'Lire la suite',
    'creditCard.holderName': 'Nom sur la carte',
    'creditCard.holderName.invalid': "Entrez le nom tel qu'il apparaît sur la carte",
    'creditCard.expiryDateField.month': 'Mois',
    'creditCard.expiryDateField.year': 'Année',
    'creditCard.storeDetailsButton': 'Enregistrer pour la prochaine fois',
    'creditCard.dualBrand.title': 'Marque de la carte',
    'creditCard.dualBrand.description':
        'Sélectionnez la marque de la carte avec laquelle vous préférez payer. Ceci est facultatif.',
    installments: 'Nombre de versements',
    installmentOption: '%{times}x %{partialValue}',
    installmentOptionMonths: '%{times} mois',
    'installments.oneTime': 'Paiement unique',
    'installments.installments': 'Paiement échelonné',
    'installments.revolving': 'Paiement en plusieurs fois',
    'sepaDirectDebit.ibanField.invalid': 'Numéro de compte non valide',
    'sepa.ownerName': 'Au nom de',
    'sepa.ibanNumber': 'Numéro de compte (IBAN)',
    'error.title': 'Erreur',
    'error.subtitle.redirect': 'Échec de la redirection',
    'error.subtitle.payment': 'Échec du paiement',
    'error.subtitle.refused': 'Paiement refusé',
    'error.message.unknown': "Une erreur inconnue s'est produite",
    'errorPanel.title': 'Erreurs existantes',
    'idealIssuer.selectField.title': 'Banque',
    'creditCard.success': 'Paiement réussi',
    loading: 'Chargement en cours...',
    continue: 'Continuer',
    continueTo: 'Poursuivre vers',
    'wechatpay.timetopay': 'Vous avez %@ pour payer cette somme',
    'sr.wechatpay.timetopay':
        'Vous disposez de %# minutes%# et %# secondes%# pour effectuer le paiement',
    'wechatpay.scanqrcode': 'Scanner le code QR',
    personalDetails: 'Informations personnelles',
    companyDetails: "Coordonnées de l'entreprise",
    'companyDetails.name': "Nom de l'entreprise",
    'companyDetails.registrationNumber': "Numéro d'enregistrement",
    socialSecurityNumber: 'Numéro de sécurité sociale',
    firstName: 'Prénom',
    'firstName.invalid': 'Entrez votre prénom',
    infix: 'Préfixe',
    lastName: 'Nom de famille',
    'lastName.invalid': 'Entrez votre nom',
    mobileNumber: 'Numéro de portable',
    'mobileNumber.invalid': 'Numéro de portable non valide',
    city: 'Ville',
    postalCode: 'Code postal',
    'postalCode.optional': 'Code postal (facultatif)',
    countryCode: 'Code pays',
    telephoneNumber: 'Numéro de téléphone',
    telephonePrefix: 'Préfixe',
    dateOfBirth: 'Date de naissance',
    shopperEmail: 'Adresse e-mail',
    gender: 'Sexe',
    'gender.notselected': 'Sélectionnez votre sexe',
    male: 'Homme',
    female: 'Femme',
    billingAddress: 'Adresse de facturation',
    street: 'Rue',
    stateOrProvince: 'État ou province',
    country: 'Pays/Région',
    houseNumberOrName: 'Numéro de rue',
    separateDeliveryAddress: 'Indiquer une adresse de livraison distincte',
    deliveryAddress: 'Adresse de livraison',
    'deliveryAddress.firstName': 'Prénom du destinataire',
    'deliveryAddress.lastName': 'Nom du destinataire',
    zipCode: 'Code postal',
    apartmentSuite: 'Appartement',
    provinceOrTerritory: 'Province ou territoire',
    cityTown: 'Ville',
    address: 'Adresse',
    'address.search.contextualText':
        'Commencez à saisir votre adresse pour effectuer une recherche',
    'address.errors.incomplete': 'Saisissez une adresse pour continuer',
    'address.enterManually': "Saisissez l'adresse manuellement",
    state: 'État',
    'field.title.optional': '(facultatif)',
    'issuerList.selectField.contextualText': 'Sélectionnez votre banque pour continuer',
    'issuerList.selectField.label': 'Choix de la banque',
    privacyPolicy: 'Politique de confidentialité',
    'afterPay.agreement': "J'accepte les %@ de Riverty",
    'riverty.termsAndConditions':
        "J'accepte les %#Conditions générales%# du mode de paiement Riverty. La Politique de confidentialité de Riverty peut être consultée %#ici%#.",
    paymentConditions: 'conditions de paiement',
    openApp: "Ouvrir l'application",
    'voucher.readInstructions': 'Lire les instructions',
    'voucher.introduction':
        'Merci pour votre achat, veuillez utiliser le coupon suivant pour finaliser votre paiement.',
    'voucher.expirationDate': "Date d'expiration",
    'voucher.alternativeReference': 'Autre référence',
    'voucher.paymentReferenceLabel': 'Référence du paiement',
    'voucher.surcharge': 'Comprend une surcharge de %@',
    'voucher.introduction.doku':
        "Nous vous remercions de votre achat. Veuillez finaliser votre paiement à l'aide des informations suivantes.",
    'voucher.shopperName': "Nom de l'acheteur",
    'voucher.merchantName': 'Marchand',
    'voucher.introduction.econtext':
        "Nous vous remercions de votre achat. Veuillez finaliser votre paiement à l'aide des informations suivantes.",
    'voucher.telephoneNumber': 'Numéro de téléphone',
    'voucher.shopperReference': 'Référence client',
    'voucher.collectionInstitutionNumber': 'Numéro du point de paiement',
    'voucher.econtext.telephoneNumber.invalid':
        'Le numéro de téléphone doit comporter 10 ou 11 chiffres',
    'boletobancario.btnLabel': 'Générer un Boleto',
    'boleto.sendCopyToEmail': 'Envoyer une copie à mon adresse e-mail',
    'button.copy': 'Copier',
    'button.copied': 'Copié !',
    'button.download': 'Télécharger',
    'boleto.socialSecurityNumber': 'CPF/CNPJ',
    'boleto.socialSecurityNumber.invalid': 'Saisissez un numéro CPF/CNPJ valide',
    'creditCard.storedCard.description.ariaLabel': 'La carte enregistrée se termine en %@',
    'voucher.entity': 'Entité',
    donateButton: 'Faire un don',
    donationTermsCondition: 'En faisant un don, vous acceptez les %#conditions générales%#.',
    donationRoundUpDescription:
        '%{donationAmount} est un montant arrondi à partir de votre paiement initial (%{originalPaymentAmount})',
    notNowButton: 'Pas maintenant',
    thanksForYourSupport: 'Merci de votre soutien !',
    'resultMessages.preauthorized': 'Détails enregistrés',
    preauthorizeWith: 'Pré-autoriser avec',
    confirmPreauthorization: 'Confirmer la pré-autorisation',
    confirmPurchase: "Confirmer l'achat",
    applyGiftcard: 'Utiliser',
    giftcardBalance: 'Solde de la carte cadeau',
    deductedBalance: 'Solde déduit',
    'creditCard.taxNumber':
        "Date de naissance du porteur de carte ou numéro d'identification de l'entreprise",
    'creditCard.taxNumber.label':
        "Date de naissance du porteur de carte (au format AAMMJJ) ou numéro d'identification de l'entreprise (à 10 chiffres)",
    'creditCard.taxNumber.labelAlt': "Numéro d'identification de l'entreprise (à 10 chiffres)",
    'creditCard.taxNumber.invalid':
        "Date de naissance du porteur de carte ou numéro d'identification de l'entreprise incorrect(e)",
    'storedPaymentMethod.disable.button': 'Supprimer',
    'storedPaymentMethod.disable.confirmation': 'Supprimer le mode de paiement enregistré',
    'storedPaymentMethod.disable.confirmButton': 'Oui, supprimer',
    'storedPaymentMethod.disable.cancelButton': 'Annuler',
    'ach.bankAccount': 'Compte bancaire',
    'ach.bankAccount.title': 'Coordonnées bancaires',
    'ach.bankAccount.label': 'Type de compte',
    'ach.bankAccount.placeholder': 'Choisissez un type de compte',
    'ach.bankAccount.nothing-selected-error': 'Choisissez un type de compte',
    'ach.bankAccount.option.personal-checking': 'Compte courant personnel',
    'ach.bankAccount.option.personal-savings': "Compte d'épargne personnel",
    'ach.bankAccount.option.business-checking': "Compte courant d'entreprise",
    'ach.bankAccount.option.business-savings': "Compte d'épargne d'entreprise",
    'ach.bankAccountNumberVerification.label': 'Vérifier le numéro de compte',
    'ach.bankAccountNumberVerification.error.not-match': 'Le numéro de compte ne correspond pas',
    'ach.bankAccountNumberVerification.error.empty':
        'Veuillez saisir le numéro de compte fourni pour vérifier le numéro de compte',
    'ach.routingNumber.label': 'Numéro de routage',
    'ach.accountHolderNameField.title': 'Nom du titulaire du compte',
    'ach.accountHolderNameField.contextualText': 'Situé sur le recto de votre carte',
    'ach.accountHolderNameField.invalid': 'Nom du titulaire du compte incorrect',
    'ach.accountNumberField.invalid': 'Numéro du compte incorrect',
    'ach.accountLocationField.invalid': 'Code ABA incorrect',
    'ach.savedBankAccount': 'Compte bancaire enregistré',
    'ach.savings': 'Compte d’épargne',
    'ach.checking': 'Compte courant',
    'select.state': "Sélectionnez l'État",
    'select.stateOrProvince': "Sélectionnez l'État ou la province",
    'select.provinceOrTerritory': 'Sélectionnez la province ou le territoire',
    'select.country': 'Sélectionnez le pays/la région',
    'select.noOptionsFound': 'Aucune option trouvée',
    'telephoneNumber.invalid': 'Numéro de téléphone incorrect',
    qrCodeOrApp: 'ou',
    'paypal.processingPayment': 'Traitement du paiement en cours...',
    generateQRCode: 'Générer un code QR',
    'await.waitForConfirmation': 'En attente de confirmation',
    'mbway.confirmPayment': "Confirmez votre paiement sur l'application MB WAY",
    'shopperEmail.invalid': 'Adresse e-mail incorrecte',
    'dateOfBirth.format': 'JJ/MM/AAAA',
    'dateOfBirth.invalid':
        'Saisissez une date de naissance valide indiquant que vous avez au moins 18 ans',
    'blik.confirmPayment': 'Ouvrez votre application bancaire pour confirmer le paiement.',
    'blik.invalid': 'Saisissez les 6 chiffres',
    'blik.code': 'Code à 6 chiffres',
    'blik.help': 'Ouvrez votre application bancaire pour obtenir le code.',
    'swish.pendingMessage':
        "Après avoir scanné le code QR, la mise à jour du statut de paiement peut prendre jusqu'à 10 minutes. Si vous effectuez une nouvelle tentative de paiement dans ce délai, cela pourrait occasionner plusieurs débits.",
    'field.valid': 'Champ valide',
    'field.invalid': 'Champ non valide',
    'field.error.required': 'Saisissez le %{label}',
    'field.error.invalid': 'Saisissez un %{label} valide',
    'error.giftcard.no-balance': "Aucun solde n'est disponible sur cette carte cadeau",
    'error.giftcard.card-error': 'Aucune carte cadeau ne correspond à ce numéro',
    'error.giftcard.currency-error':
        'Les cartes cadeaux sont valables uniquement dans la devise dans laquelle elles ont été émises',
    'err.gen.9100': 'Champ incomplet',
    'err.gen.9101': 'Champ non valide',
    'err.gen.9102': "Erreur d'analyse des données",
    '3ds.chal.805': 'Problème résolu sans objet %{result}',
    'cc.num.902': 'Entrez un numéro de carte valide',
    'cc.num.900': 'Entrez le numéro de la carte',
    'cc.num.903': 'Entrez un émetteur de carte pris en charge',
    'cc.num.901': 'Entrez le numéro de carte complet',
    'cc.dat.912': "Entrez une date d'expiration valide",
    'cc.dat.913': "Entrez une date d'expiration valide",
    'cc.dat.914': 'La carte de crédit arrive à expiration',
    'cc.dat.910': "Entrez la date d'expiration",
    'cc.dat.911': "Entrez la date d'expiration complète",
    'cc.mth.915': "Entrez le mois d'expiration",
    'cc.yr.917': "Entrez l'année d'expiration",
    'cc.yr.918': "Entrez l'année d'expiration complète",
    'cc.cvc.920': 'Entrez le code de sécurité',
    'cc.cvc.921': 'Entrez le code de sécurité complet',
    'ach.num.945': 'Saisissez le numéro de compte bancaire',
    'ach.num.946': 'Saisissez le numéro de compte bancaire complet',
    'ach.loc.947': 'Saisissez le numéro de routage bancaire',
    'ach.loc.948': 'Saisissez le numéro de routage bancaire complet',
    'kcp.pwd.940': 'Saisissez le mot de passe',
    'kcp.pwd.941': 'Saisissez le mot de passe complet',
    'amazonpay.signout': "Se déconnecter d'Amazon",
    'amazonpay.changePaymentDetails': 'Modifier les informations de paiement',
    'partialPayment.warning': 'Sélectionnez un autre mode de paiement pour régler le solde',
    'partialPayment.remainingBalance': 'Le solde restant sera de %{amount}',
    'bankTransfer.beneficiary': 'Nom du bénéficiaire',
    'bankTransfer.iban': 'IBAN',
    'bankTransfer.bic': 'BIC',
    'bankTransfer.reference': 'Référence',
    'bankTransfer.accountNumber': 'Numéro du compte',
    'bankTransfer.sortCode': 'Code de tri (sort code)',
    'bankTransfer.routingNumber': 'Numéro de routage',
    'bankTransfer.branchCode': "Code de l'agence",
    'bankTransfer.bankCode': 'Code banque',
    'bankTransfer.introduction':
        "Appuyez sur « Continuer » pour créer un nouveau paiement par virement bancaire. Utilisez les informations de l'écran suivant pour finaliser ce paiement.",
    'bankTransfer.instructions':
        'Merci pour votre achat ! Veuillez utiliser les informations suivantes pour finaliser votre paiement.',
    'bankTransfer.shopperEmail.contextualText':
        'Une copie des informations de paiement vous sera envoyée à votre adresse e-mail afin que vous puissiez finaliser le paiement ultérieurement.',
    'bankTransfer.details.title': 'Informations de paiement',
    'bankTransfer.instruction.title': 'Processus de paiement',
    'bankTransfer.instruction.step1': 'Ouvrez votre application bancaire',
    'bankTransfer.instruction.step2': 'Commencez un nouveau transfert',
    'bankTransfer.instruction.step3': 'Saisissez les détails du compte indiqués ci-dessous',
    'bankTransfer.instruction.step4': 'Vérifiez et confirmez le paiement',
    'bacs.accountHolderName': 'Nom du titulaire du compte bancaire',
    'bacs.accountHolderName.invalid': 'Nom du titulaire du compte bancaire incorrect',
    'bacs.accountNumber': 'Numéro du compte bancaire',
    'bacs.accountNumber.invalid': 'Numéro du compte bancaire incorrect',
    'bacs.bankLocationId': 'Code de tri (sort code)',
    'bacs.bankLocationId.invalid': 'Code de tri (sort code) non valide',
    'bacs.consent.amount': "J'accepte que le montant ci-dessus soit déduit de mon compte bancaire.",
    'bacs.consent.account':
        "Je confirme être le titulaire du compte et qu'aucune autre signature que la mienne n'est requise pour autoriser un prélèvement sur ce compte.",
    edit: 'Modifier',
    'bacs.confirm': 'Confirmer et payer',
    'bacs.result.introduction': 'Téléchargez votre mandat de prélèvement (DDI)',
    'download.pdf': 'Télécharger le PDF',
    'creditCard.encryptedCardNumber.aria.iframeTitle': 'Iframe pour le numéro de carte',
    'creditCard.encryptedExpiryDate.aria.iframeTitle': "Iframe pour la date d'expiration",
    'creditCard.encryptedExpiryMonth.aria.iframeTitle': "Iframe pour le mois d'expiration",
    'creditCard.encryptedExpiryYear.aria.iframeTitle': "Iframe pour l'année d'expiration",
    'creditCard.encryptedSecurityCode.aria.iframeTitle': 'Iframe pour le code de sécurité',
    'creditCard.encryptedPassword.aria.iframeTitle': 'Iframe pour le mot de passe',
    'giftcard.encryptedCardNumber.aria.iframeTitle': 'Iframe pour le numéro de carte',
    'giftcard.encryptedSecurityCode.aria.iframeTitle': 'Iframe pour le code secret',
    giftcardTransactionLimit:
        'Montant maximum autorisé par transaction avec cette carte cadeau : %{amount}',
    'ach.encryptedBankAccountNumber.aria.iframeTitle': 'Iframe pour le numéro de compte bancaire',
    'ach.encryptedBankLocationId.aria.iframeTitle': 'Iframe pour le numéro de routage bancaire',
    'pix.instructions':
        'Utilisez votre application bancaire pour scanner le code QR ou copiez le code PIX ci-dessous pour finaliser votre paiement.',
    'pix.code.label': 'Code PIX',
    'pix.code.copy.label': 'Copier le code PIX',
    'pix.code.copied.label': 'Code PIX copié',
    'twint.saved': 'stocké',
    orPayWith: 'ou payez avec',
    'invalid.format.expects': '%{label} Format non valide. Format requis : %{format}',
    'upi.qrCodeWaitingMessage':
        "Scannez le code QR à l'aide de votre application UPI préférée pour effectuer le paiement",
    'upi.vpaWaitingMessage': 'Ouvrez votre application UPI pour confirmer le paiement',
    'upi.modeSelection': 'Comment souhaitez-vous utiliser UPI ?',
    'upi.completePayment': 'Terminez votre paiement',
    'upi.mode.enterUpiId': 'Identifiant UPI',
    'upi.mode.qrCode': 'Code QR',
    'upi.mode.payByAnyUpi': 'Application UPI',
    'upi.collect.dropdown.label': "Entrez l'identifiant UPI",
    'upi.collect.instruction': 'Entrez votre identifiant UPI',
    'upi.collect.field.label': 'Identifiant UPI',
    'upi.collect.field.contextualText':
        "Saisissez l'identifiant UPI associé à votre compte et appuyez sur Continuer",
    'upi.collect.field.invalid-id-error': 'Saisissez un identifiant UPI valide',
    'upi.error.noAppSelected': 'Sélectionnez votre application UPI préférée pour continuer',
    'upi.intent.instruction': 'Sélectionnez votre application UPI préférée',
    'upi.qrCode.instruction':
        'Après avoir généré le code QR, vous pouvez utiliser votre application UPI préférée pour effectuer le paiement.',
    'onlineBanking.termsAndConditions': 'En continuant, vous acceptez les %#conditions générales%#',
    'onlineBankingPL.termsAndConditions':
        "En continuant, vous acceptez les %#réglementations%# et l'%#obligation d'information%# de Przelewy24",
    'ctp.loading.poweredByCtp': 'Avec la technologie Click to Pay',
    'ctp.loading.intro': 'Nous vérifions si vous avez des cartes enregistrées avec Click to Pay...',
    'ctp.login.title': 'Accéder à Click to Pay',
    'ctp.login.subtitle': "Saisissez l'adresse e-mail de votre profil Click to Pay pour continuer.",
    'ctp.login.inputLabel': 'E-mail',
    'ctp.logout.notYou': "Ce n'est pas votre compte ?",
    'ctp.logout.notYourCards': 'Ce ne sont pas vos cartes ?',
    'ctp.logout.notYourCard': "Ce n'est pas votre carte ?",
    'ctp.logout.notYourProfile': "Ce n'est pas votre profil ?",
    'ctp.otp.fieldLabel': 'Code à usage unique',
    'ctp.otp.resendCode': 'Renvoyer le code',
    'ctp.otp.codeResent': 'Code renvoyé',
    'ctp.otp.title': 'Accédez à vos cartes Click to Pay',
    'ctp.otp.subtitle': 'Saisissez le code %@ envoyé à %@ pour vérifier votre identité.',
    'ctp.otp.saveCookiesCheckbox.label': 'Ignorer la vérification la prochaine fois',
    'ctp.otp.saveCookiesCheckbox.information':
        "Sélectionnez cette option pour mémoriser les informations sur votre appareil et votre navigateur afin d'accélérer le paiement dans les magasins participants. Non recommandé pour les appareils partagés.",
    'ctp.otp.saveCookiesCheckbox.shorterInfo':
        'Sélectionner cette option pour mémoriser les informations sur votre appareil et votre navigateur',
    'ctp.emptyProfile.message': 'Aucune carte enregistrée dans ce profil Click to Pay',
    'ctp.separatorText': 'ou utilisez',
    'ctp.cards.title': 'Terminez le paiement avec Click to Pay',
    'ctp.cards.subtitle': 'Sélectionnez la carte à utiliser.',
    'ctp.cards.expiredCard': 'Expiré',
    'ctp.manualCardEntry': 'Saisie manuelle de la carte',
    'ctp.aria.infoModalButton': "Qu'est-ce que Click to Pay ?",
    'ctp.infoPopup.title': 'Click to Pay offre la simplicité du sans contact sur Internet',
    'ctp.infoPopup.subtitle':
        'Un mode de paiement rapide et sécurisé compatible avec les cartes Mastercard, Visa et autres fournisseurs.',
    'ctp.infoPopup.benefit1':
        'Click to Pay utilise le chiffrement pour assurer la sécurité de vos informations',
    'ctp.infoPopup.benefit2': 'Utilisez-le pour vos achats dans le monde entier',
    'ctp.infoPopup.benefit3':
        'Enregistrez votre carte une fois et simplifiez vos paiements pour toujours',
    'ctp.errors.AUTH_INVALID': 'Authentification non valide',
    'ctp.errors.NOT_FOUND':
        "Aucun compte trouvé, vérifiez l'adresse e-mail ou saisissez manuellement les informations de votre carte",
    'ctp.errors.ID_FORMAT_UNSUPPORTED': 'Format non pris en charge',
    'ctp.errors.FRAUD': 'Le compte utilisateur a été verrouillé ou désactivé',
    'ctp.errors.CONSUMER_ID_MISSING': "L'identité du client n'est pas indiquée dans la demande",
    'ctp.errors.ACCT_INACCESSIBLE':
        "Ce compte n'est pas disponible actuellement car il est verrouillé",
    'ctp.errors.CODE_INVALID': 'Code de vérification incorrect',
    'ctp.errors.CODE_EXPIRED': 'Ce code a expiré',
    'ctp.errors.RETRIES_EXCEEDED':
        "La limite du nombre de tentatives pour la génération de l'OTP a été dépassée",
    'ctp.errors.OTP_SEND_FAILED': "Impossible d'envoyer l'OTP au destinataire",
    'ctp.errors.REQUEST_TIMEOUT':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'ctp.errors.UNKNOWN_ERROR':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'ctp.errors.SERVICE_ERROR':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'ctp.errors.SERVER_ERROR':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'ctp.errors.INVALID_PARAMETER':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'ctp.errors.AUTH_ERROR':
        "Une erreur s'est produite, réessayez ou saisissez manuellement les informations de votre carte",
    'paymentMethodsList.aria.label': 'Choisissez un mode de paiement',
    'paymentMethodsList.storedPayments.label': 'Modes de paiement enregistrés',
    'paymentMethodsList.otherPayments.label': "Plus d'options de paiement",
    'paymentMethodsList.otherPayments.buttonLabel': 'Afficher %{count} de plus',
    'paymentMethodsList.promotedPayments.label': 'autre',
    'companyDetails.name.invalid': "Entrez le nom de l'entreprise",
    'companyDetails.registrationNumber.invalid': "Entrez le numéro d'identification",
    'consent.checkbox.invalid': 'Vous devez accepter les conditions générales',
    'form.instruction': 'Tous les champs sont obligatoires, sauf indication contraire.',
    'trustly.descriptor': 'Paiement bancaire instantané',
    'trustly.description1':
        "Payez directement à partir de n'importe lequel de vos comptes bancaires, avec une sécurité de niveau bancaire",
    'trustly.description2': "Aucune carte, aucun téléchargement d'application, aucune inscription",
    'ancv.input.label': 'Votre identification ANCV',
    'ancv.confirmPayment': 'Utilisez votre application ANCV pour confirmer le paiement.',
    'ancv.form.instruction':
        "L'application Chèque-Vacances est nécessaire pour valider ce paiement.",
    'ancv.beneficiaryId.invalid': 'Saisissez une adresse e-mail ou un identifiant ANCV valide',
    'creditCard.cardNumber.label': 'Numéro de la carte',
    'creditCard.expiryDate.label': "Date d'expiration",
    'creditCard.expiryDate.contextualText': 'Recto de la carte au format MM/AA',
    'creditCard.securityCode.label': 'Code de sécurité',
    'creditCard.securityCode.label.optional': 'Code de sécurité (facultatif)',
    'creditCard.securityCode.contextualText.3digits': '3 chiffres sur le verso de la carte',
    'creditCard.securityCode.contextualText.4digits': '4 chiffres sur le recto de la carte',
    'creditCard.expiryMonth.label': "Mois d'expiration",
    'creditCard.expiryYear.label': "Année d'expiration",
    'creditCard.pin.label': 'PIN',
    'creditCard.password.label': 'Les deux premiers chiffres du code de votre carte',
    'giftcard.cardNumber.label': 'Numéro de la carte',
    'giftcard.expiryDate.label': "Date d'expiration",
    'giftcard.securityCode.label': 'PIN',
    'ach.bankAccountNumber.label': 'Numéro du compte',
    'ach.bankLocationId.label': 'Code ABA',
    'dragonpayVoucher.selectField.contextualText.nonBank': 'Sélectionnez votre fournisseur',
    'dragonpayVoucher.selectField.contextualText.bank': 'Sélectionnez votre banque',
    'payme.openPayMeApp':
        "Finalisez votre paiement dans l'application PayMe en autorisant le paiement dans l'application, puis attendez la confirmation.",
    'payme.redirectButtonLabel': "Ouvrir l'application PayMe",
    'payme.scanQrCode': 'Effectuez votre paiement avec un code QR',
    'payme.timeToPay': 'Ce code QR est valide pendant %@',
    'payme.instructions.steps':
        "Ouvrez l'application PayMe.%@Scannez le code QR pour autoriser le paiement.%@Effectuez le paiement dans l'application et attendez la confirmation.",
    'payme.instructions.footnote':
        'Veuillez ne pas fermer cette page avant que le paiement ne soit terminé.',
    'payByBankAISDD.disclaimer.header':
        "Utilisez Pay by Bank pour payer instantanément depuis n'importe quel compte bancaire.",
    'payByBankAISDD.disclaimer.body':
        "En connectant votre compte bancaire, vous autorisez le débit de tous les montants dus pour l'utilisation de nos services et l'achat de nos produits, jusqu'à révocation de votre autorisation.",
    'paymentMethodBrand.other': 'autre',
    'paynow.scanQrCode':
        "Scannez le code QR à l'aide de l'application PayNow pour effectuer le paiement",
    'paynow.mobileViewInstruction.step1': "Faites une capture d'écran du code QR.",
    'paynow.mobileViewInstruction.step2': "Ouvrez l'application bancaire ou de paiement PayNow.",
    'paynow.mobileViewInstruction.step3': "Sélectionnez l'option permettant de scanner un code QR.",
    'paynow.mobileViewInstruction.step4':
        "Choisissez l'option pour télécharger un code QR et sélectionnez la capture d'écran.",
    'paynow.mobileViewInstruction.step5': 'Complétez la transaction.',
    'payto.payid.description':
        'Veuillez saisir le PayID et les détails du compte qui sont connectés à votre compte PayTo.',
    'payto.payid.label.identifier': 'Identifiant',
    'payto.payid.label.orgid': "Identifiant de l'organisation",
    'payto.payid.option.phone': 'Portable',
    'payto.payid.option.email': 'E-mail',
    'payto.payid.option.orgid': "Identifiant de l'organisation",
    'payto.bsb.description':
        'Saisissez le numéro de compte bancaire et la succursale bancaire qui est liée à votre compte pour continuer.',
    'payto.bsb.label.bankAccountNumber': 'Numéro du compte bancaire',
    'payto.instructions.steps':
        "Connectez-vous à votre application bancaire PayTo@Autorisez l'accord PayTo@Approuvez les conditions de paiement@Finalisez la transaction",
    'payto.confirmPayment':
        'Nous vous remercions de votre achat. Veuillez finaliser votre paiement en suivant les instructions ci-dessous.',
    'payto.mandate.amount.label': 'Montant',
    'payto.mandate.frequency.label': 'Fréquence',
    'payto.mandate.remarks.label': 'Description',
    'payto.mandate.startsAt.label': 'Date du premier paiement',
    'payto.mandate.endsAt.label': 'Date de fin',
    'payto.mandate.payee.label': 'Bénéficiaire',
    'payto.mandate.amount.max': "Jusqu'à %{amount} par transaction",
    'payto.mandate.frequency.adhoc-no-count': 'Ad Hoc',
    'payto.mandate.frequency.adhoc': '%{count} fois',
    'payto.mandate.frequency.daily': '%{count} paiement(s) par jour',
    'payto.mandate.frequency.weekly': '%{count} paiement(s) par semaine',
    'payto.mandate.frequency.biWeekly': '%{count} paiement(s) toutes les deux semaines',
    'payto.mandate.frequency.monthly': '%{count} paiement(s) mensuel(s)',
    'payto.mandate.frequency.quarterly': '%{count} paiement(s) par trimestre',
    'payto.mandate.frequency.halfYearly': '%{count} paiement(s) par semestre',
    'payto.mandate.frequency.yearly': '%{count} paiement(s) par an',
    'payto.label.firstName': 'Prénom du titulaire du compte',
    'payto.label.lastName': 'Nom de famille du titulaire du compte',
    'payto.await.waitForConfirmation': 'En attente de votre approbation',
    'payto.bsb.option.label': 'Numéro BSB et numéro de compte',
    'card.fastlane.a11y.logo': 'Logo Fastlane by PayPal',
    'card.fastlane.a11y.openDialog': 'En savoir plus sur Fastlane',
    'card.fastlane.a11y.closeDialog': 'Fermer la boîte de dialogue',
    'card.fastlane.mobileInputLabel': 'Numéro de portable',
    'card.fastlane.consentToggle':
        'Enregistrez vos informations avec Fastlane pour accélérer les paiements et plus encore',
    'card.fastlane.consentText':
        'En enregistrant vos informations, vous acceptez de recevoir des codes par SMS pour utiliser Fastlane partout où il est disponible. Fastlane enverra un SMS au numéro fourni. Vous acceptez également ces %#conditions%# et la %#déclaration de confidentialité%#.',
    'card.fastlane.modal.benefit1.header': 'Remplissez automatiquement vos paiements',
    'card.fastlane.modal.benefit1.text':
        'Obtenez des codes à usage unique pour utiliser les cartes et les adresses que vous avez enregistrées.',
    'card.fastlane.modal.benefit2.header': 'Protégez vos informations',
    'card.fastlane.modal.benefit2.text':
        "Vos informations de paiement sont chiffrées lorsqu'elles sont stockées et envoyées aux endroits où vous faites vos achats.",
    'card.fastlane.modal.benefit3.header': 'Utiliser dans tous les magasins',
    'card.fastlane.modal.benefit3.text':
        'Accélérez les paiements partout où Fastlane est disponible.',
    'paybybankpix.redirectBtn.label': 'Continuer vers PIX OpenBanking',
    'paybybankpix.issuerList.disclaimer':
        'En continuant, vous acceptez la %#Déclaration de consentement%# et serez redirigé vers votre banque.',
    'paybybankpix.issuerList.selectField.label': 'Banque',
    'paybybankpix.issuerList.introduction.logo.content':
        'Connectez vos clés PIX préférées pour effectuer des paiements via Open Finance',
    'paybybankpix.await.waitForConfirmation': 'En attente de votre confirmation…',
    'paybybankpix.await.timeToPay': 'Ce processus prendra environ %{numberOfMin} minute(s)',
    'paybybankpix.await.withOpenFinance': "Avec la facilité d'Open Finance",
    'paybybankpix.await.logoAlt.openFinance': 'Open Finance',
    'paybybankpix.storedPayment.payButton.label': 'Poursuivre le paiement',
    'paybybankpix.storedPayment.additionalLabel': 'Compte %{receiver} via Pix',
    'paybybankpix.await.fetchDetails': 'Récupération des détails…',
    'eftpad-canada.input.accountHolderName.label': 'Nom du titulaire du compte',
    'eftpad-canada.input.accountHolderName.error': 'Saisissez le nom du titulaire du compte',
    'eftpad-canada.input.accountNumber.label': 'Numéro du compte',
    'eftpad-canada.input.accountNumber.contextualText': 'Entre 7 et 12 chiffres',
    'eftpad-canada.input.accountNumber.error':
        'Saisissez un numéro de compte compris entre 7 et 12 chiffres',
    'eftpad-canada.input.institutionNumber.label': "Numéro d'institution",
    'eftpad-canada.input.institutionNumber.contextualText': '3 chiffres',
    'eftpad-canada.input.institutionNumber.error':
        "Entrez un numéro d'institution composé de 3 chiffres",
    'eftpad-canada.input.transitNumber.label': 'Numéro de transit',
    'eftpad-canada.input.transitNumber.contextualText': '5 chiffres',
    'eftpad-canada.input.transitNumber.error':
        'Saisissez un numéro de transit composé de 5 chiffres',
    'eftpad-canada.settlement-info':
        'Les fonds seront retirés de votre compte bancaire sous 1 à 3 jours ouvrables.',
    'upi.mandate.intro': 'Vous configurez actuellement un paiement récurrent UPI Autopay',
    'upi.mandate.max.extraText':
        'Vous allez approuver une limite plus élevée pour autoriser de futurs changements de forfait',
    'upi.mandate.upTo': "jusqu'à %{amount}",
    'upi.mandate.frequency.monthly': '/mois',
    'upi.mandate.frequency.weekly': '/semaine',
    'upi.mandate.frequency.adhoc': ' tel que présenté'
}
const adyenIt = {
    'creditCard.holderName.invalid': 'Immetti il nome riportato sulla carta',
    'form.instruction': 'Se non diversamente indicato, tutti i campi sono obbligatori.',
    'creditCard.holderName': 'Titolare carta',
    'creditCard.numberField.title': 'Numero carta',
    'creditCard.expiryDateField.title': 'Data di scadenza',
    'creditCard.cvcField.title': 'Codice di sicurezza',
    storeDetails: 'Salva per il prossimo pagamento',
    'creditCard.cvcField.placeholder.3digits': '3 cifre',
    'creditCard.expiryDateField.placeholder': 'MM/AA',
    continueTo: 'Procedi verso',
    'error.va.sf-cc-num.01': 'Immetti un numero di carta valido',
    'error.va.sf-cc-num.02': 'Immetti il numero della carta',
    'error.va.sf-cc-num.03': 'Immetti un marchio di carta supportato',
    'error.va.sf-cc-num.04': 'Immetti il numero completo della carta',
    'error.va.sf-cc-dat.01': 'Immetti una data di scadenza valida',
    'error.va.sf-cc-dat.02': 'Immetti una data di scadenza valida',
    'error.va.sf-cc-dat.03': 'Carta di credito in scadenza',
    'error.va.sf-cc-dat.04': 'Immetti la data di scadenza',
    'error.va.sf-cc-dat.05': 'Immetti la data di scadenza completa',
    'error.va.sf-cc-mth.01': 'Immetti il mese di scadenza',
    'error.va.sf-cc-yr.01': "Immetti l'anno di scadenza",
    'error.va.sf-cc-yr.02': "Immetti l'anno di scadenza completo",
    'error.va.sf-cc-cvc.01': 'Immetti il codice di sicurezza',
    'error.va.sf-cc-cvc.02': 'Immetti il codice di sicurezza completo',
    payButton: 'Paga',
    'payButton.redirecting': 'Reindirizzamento...',
    'payButton.with': 'Paga %{value} con %{maskedData}',
    'payButton.saveDetails': 'Salva dettagli',
    close: 'Chiudi',
    'creditCard.expiryDateField.month': 'Mese',
    'creditCard.expiryDateField.year': 'Anno',
    'creditCard.storeDetailsButton': 'Ricorda per la prossima volta',
    'creditCard.dualBrand.title': 'Marca della carta',
    'creditCard.dualBrand.description':
        'Seleziona la marca della carta con cui preferisci pagare. Questo è facoltativo.',
    installments: 'Numero di rate',
    installmentOption: '%{partialValue} x%{times}',
    installmentOptionMonths: '%{times} mesi',
    'installments.oneTime': 'Pagamento una tantum',
    'installments.installments': 'Pagamento rateale',
    'installments.revolving': 'Pagamento ricorrente',
    'sepaDirectDebit.ibanField.invalid': 'Numero di conto non valido',
    'sepa.ownerName': 'Nome Intestatario Conto',
    'sepa.ibanNumber': 'Numero di conto (IBAN)',
    'error.title': 'Errore',
    'error.subtitle.redirect': 'Reindirizzamento non riuscito',
    'error.subtitle.payment': 'Pagamento non riuscito',
    'error.subtitle.refused': 'Pagamento respinto',
    'error.message.unknown': 'Si è verificato un errore sconosciuto',
    'errorPanel.title': 'Errori presenti',
    'idealIssuer.selectField.title': 'Banca',
    'creditCard.success': 'Pagamento riuscito',
    loading: 'Caricamento in corso...',
    continue: 'Continua',
    'wechatpay.timetopay': 'Devi pagare %@',
    'sr.wechatpay.timetopay': 'Hai %#minuti%# %#secondi%# per pagare',
    'wechatpay.scanqrcode': 'Scansiona il codice QR',
    personalDetails: 'Dati personali',
    companyDetails: "Informazioni dell'azienda",
    'companyDetails.name': 'Ragione sociale',
    'companyDetails.registrationNumber': 'Numero di registrazione',
    socialSecurityNumber: 'Numero di previdenza sociale',
    firstName: 'Nome',
    'firstName.invalid': 'Immetti il tuo nome',
    infix: 'Prefisso',
    lastName: 'Cognome',
    'lastName.invalid': 'Immetti il tuo cognome',
    mobileNumber: 'Numero di cellulare',
    'mobileNumber.invalid': 'Numero di cellulare non valido',
    city: 'Città',
    postalCode: 'Codice postale',
    'postalCode.optional': 'Codice postale (facoltativo)',
    countryCode: 'Codice nazionale',
    telephoneNumber: 'Numero di telefono',
    telephonePrefix: 'Prefisso',
    dateOfBirth: 'Data di nascita',
    shopperEmail: 'Indirizzo e-mail',
    gender: 'Sesso',
    'gender.notselected': 'Seleziona il tuo sesso',
    male: 'Uomo',
    female: 'Donna',
    billingAddress: 'Indirizzo di fatturazione',
    street: 'Via',
    stateOrProvince: 'Stato o provincia',
    country: 'Paese/Regione',
    houseNumberOrName: 'Numero civico',
    separateDeliveryAddress: 'Specifica un indirizzo di consegna alternativo',
    deliveryAddress: 'Indirizzo di consegna',
    'deliveryAddress.firstName': 'Nome del destinatario',
    'deliveryAddress.lastName': 'Cognome del destinatario',
    zipCode: 'CAP',
    apartmentSuite: 'Appartamento/suite',
    provinceOrTerritory: 'Provincia o territorio',
    cityTown: 'Città',
    address: 'Indirizzo',
    'address.search.contextualText': 'Digita per iniziare a cercare il tuo indirizzo',
    'address.errors.incomplete': 'Inserisci un indirizzo per continuare',
    'address.enterManually': "Inserisci l'indirizzo manualmente",
    state: 'Stato',
    'field.title.optional': '(facoltativo)',
    'issuerList.selectField.contextualText': 'Per continuare, seleziona la tua banca',
    'issuerList.selectField.label': 'Selezione della banca',
    privacyPolicy: 'Informativa sulla privacy',
    'afterPay.agreement': 'Accetto i %@ di Riverty',
    'riverty.termsAndConditions':
        "Accetto con i %#Termini e le condizioni%# generali per il metodo di Pagamento Riverty. L'informativa sulla privacy di Riverty è disponibile %#qui%#.",
    paymentConditions: 'termini di pagamento',
    openApp: "Apri l'app",
    'voucher.readInstructions': 'Leggi le istruzioni',
    'voucher.introduction':
        'Grazie per il tuo acquisto, utilizza il seguente coupon per completare il pagamento.',
    'voucher.expirationDate': 'Data di scadenza',
    'voucher.alternativeReference': 'Riferimento alternativo',
    'voucher.paymentReferenceLabel': 'Riferimento del pagamento',
    'voucher.surcharge': 'Include un sovrapprezzo di %@',
    'voucher.introduction.doku':
        'Grazie per il tuo acquisto, utilizza i seguenti dati per completare il pagamento.',
    'voucher.shopperName': "Nome dell'acquirente",
    'voucher.merchantName': 'Esercente',
    'voucher.introduction.econtext':
        'Grazie per il tuo acquisto, utilizza i seguenti dati per completare il pagamento.',
    'voucher.telephoneNumber': 'Numero di telefono',
    'voucher.shopperReference': "Riferimento dell'acquirente",
    'voucher.collectionInstitutionNumber': 'Codice identificativo del negozio',
    'voucher.econtext.telephoneNumber.invalid':
        'Il numero di telefono deve essere di 10 o 11 cifre',
    'boletobancario.btnLabel': 'Genera Boleto',
    'boleto.sendCopyToEmail': 'Invia una copia alla mia e-mail',
    'button.copy': 'Copia',
    'button.copied': 'Copiato!',
    'button.download': 'Scarica',
    'boleto.socialSecurityNumber': 'CPF/CNPJ',
    'boleto.socialSecurityNumber.invalid': 'Immetti un numero CPF/CNPJ valido',
    'creditCard.storedCard.description.ariaLabel': 'La carta memorizzata termina in %@',
    'voucher.entity': 'Entità',
    donateButton: 'Dona',
    donationTermsCondition: 'Donando accetti i %#termini e le condizioni%#',
    donationRoundUpDescription:
        '%{donationAmount} è un arrotondamento del pagamento originale (%{originalPaymentAmount})',
    notNowButton: 'Non ora',
    thanksForYourSupport: 'Grazie per il tuo sostegno!',
    'resultMessages.preauthorized': 'Dettagli salvati',
    preauthorizeWith: 'Preautorizza con',
    confirmPreauthorization: 'Conferma preautorizzazione',
    confirmPurchase: 'Conferma acquisto',
    applyGiftcard: 'Riscatta',
    giftcardBalance: 'Saldo del buono',
    deductedBalance: 'Importo detratto',
    'creditCard.taxNumber':
        'Data di nascita del titolare della carta o numero di registrazione aziendale',
    'creditCard.taxNumber.label':
        'Data di nascita del titolare della carta (AAMMGG) o numero di registrazione aziendale (10 cifre)',
    'creditCard.taxNumber.labelAlt': 'Numero di registrazione aziendale (10 cifre)',
    'creditCard.taxNumber.invalid':
        'Data di nascita del titolare della carta o numero di registrazione aziendale non validi',
    'storedPaymentMethod.disable.button': 'Rimuovi',
    'storedPaymentMethod.disable.confirmation': 'Rimuovi il metodo di pagamento archiviato',
    'storedPaymentMethod.disable.confirmButton': 'Sì, rimuoverli',
    'storedPaymentMethod.disable.cancelButton': 'Cancella',
    'ach.bankAccount': 'Conto bancario',
    'ach.bankAccount.title': 'Dettagli conto bancario',
    'ach.bankAccount.label': 'Tipo di conto',
    'ach.bankAccount.placeholder': 'Scegli un tipo di conto',
    'ach.bankAccount.nothing-selected-error': 'Scegli un tipo di conto',
    'ach.bankAccount.option.personal-checking': 'Conto corrente personale',
    'ach.bankAccount.option.personal-savings': 'Conto di risparmio personale',
    'ach.bankAccount.option.business-checking': 'Conto corrente aziendale',
    'ach.bankAccount.option.business-savings': 'Conto di risparmio aziendale',
    'ach.bankAccountNumberVerification.label': 'Verifica numero di conto',
    'ach.bankAccountNumberVerification.error.not-match': 'Il numero di conto non è corretto',
    'ach.bankAccountNumberVerification.error.empty':
        'Immettere il numero di conto fornito per verificare il numero di conto.',
    'ach.routingNumber.label': 'Numero di routing',
    'ach.accountHolderNameField.title': 'Nome del titolare del conto',
    'ach.accountHolderNameField.contextualText': 'Si trova sulla parte anteriore della carta',
    'ach.accountHolderNameField.invalid': 'Nome del titolare del conto non valido',
    'ach.accountNumberField.invalid': 'Numero di conto non valido',
    'ach.accountLocationField.invalid': 'Codice ABA non valido',
    'ach.savedBankAccount': 'Conto corrente salvato',
    'ach.savings': 'Conto di risparmio',
    'ach.checking': 'Conto corrente',
    'select.state': 'Seleziona stato',
    'select.stateOrProvince': 'Seleziona stato o provincia',
    'select.provinceOrTerritory': 'Seleziona provincia o territorio',
    'select.country': 'Seleziona paese/regione',
    'select.noOptionsFound': 'Nessuna opzione trovata',
    'telephoneNumber.invalid': 'Numero di telefono non valido',
    qrCodeOrApp: 'o',
    'paypal.processingPayment': 'Elaborazione del pagamento in corso...',
    generateQRCode: 'Genera codice QR',
    'await.waitForConfirmation': 'In attesa di conferma',
    'mbway.confirmPayment': "Conferma il pagamento con l'app MB WAY",
    'shopperEmail.invalid': 'Indirizzo e-mail non valido',
    'dateOfBirth.format': 'GG/MM/AAAA',
    'dateOfBirth.invalid':
        'Inserisci una data di nascita valida in cui risulti che hai almeno 18 anni.',
    'blik.confirmPayment': "Apri l'app della tua banca per confermare il pagamento.",
    'blik.invalid': 'Inserisci 6 numeri',
    'blik.code': 'Codice a 6 cifre',
    'blik.help': 'Ottieni il codice dalla app della tua banca.',
    'swish.pendingMessage':
        'In seguito alla scansione, lo stato della transazione può rimanere in sospeso per un massimo di 10 minuti. Un nuovo tentativo di pagamento durante questo lasso di tempo può comportare pagamenti multipli.',
    'field.valid': 'Campo valido',
    'field.invalid': 'Campo non valido',
    'field.error.required': 'Digita %{label}',
    'field.error.invalid': 'Inserisci una %{label} valida',
    'error.giftcard.no-balance': 'Questo buono regalo ha un saldo pari a zero',
    'error.giftcard.card-error':
        'Non abbiamo alcun buono regalo con questo numero nei nostri archivi',
    'error.giftcard.currency-error':
        'I buono regalo sono validi solo nella valuta in cui sono state emessi',
    'err.gen.9100': 'Campo incompleto',
    'err.gen.9101': 'Campo non valido',
    'err.gen.9102': 'Errore di parsing dei dati',
    '3ds.chal.805': 'Sfida risolta senza un oggetto %{result}',
    'cc.num.902': 'Immetti un numero di carta valido',
    'cc.num.900': 'Immetti il numero della carta',
    'cc.num.903': 'Immetti un marchio di carta supportato',
    'cc.num.901': 'Immetti il numero completo della carta',
    'cc.dat.912': 'Immetti una data di scadenza valida',
    'cc.dat.913': 'Immetti una data di scadenza valida',
    'cc.dat.914': 'Carta di credito in scadenza',
    'cc.dat.910': 'Immetti la data di scadenza',
    'cc.dat.911': 'Immetti la data di scadenza completa',
    'cc.mth.915': 'Immetti il mese di scadenza',
    'cc.yr.917': "Immetti l'anno di scadenza",
    'cc.yr.918': "Immetti l'anno di scadenza completo",
    'cc.cvc.920': 'Immetti il codice di sicurezza',
    'cc.cvc.921': 'Immetti il codice di sicurezza completo',
    'ach.num.945': 'Immetti il numero di conto corrente bancario',
    'ach.num.946': 'Immetti il numero completo del conto bancario',
    'ach.loc.947': 'Immetti il numero di routing della banca',
    'ach.loc.948': 'Immetti il numero di routing bancario completo',
    'kcp.pwd.940': 'Immetti la password',
    'kcp.pwd.941': 'Immetti la password completa',
    'amazonpay.signout': 'Esci da Amazon',
    'amazonpay.changePaymentDetails': 'Modifica i dettagli di pagamento',
    'partialPayment.warning':
        "Seleziona un altro metodo di pagamento per pagare l'importo rimanente",
    'partialPayment.remainingBalance': 'Il saldo rimanente sarà di %{amount}',
    'bankTransfer.beneficiary': 'Nome del beneficiario',
    'bankTransfer.iban': 'IBAN',
    'bankTransfer.bic': 'BIC',
    'bankTransfer.reference': 'Riferimento',
    'bankTransfer.accountNumber': 'Numero di conto',
    'bankTransfer.sortCode': 'Sort code',
    'bankTransfer.routingNumber': 'Numero di routing',
    'bankTransfer.branchCode': 'Codice filiale',
    'bankTransfer.bankCode': 'Codice bancario',
    'bankTransfer.introduction':
        'Premi "Continua" per creare un nuovo pagamento tramite bonifico bancario. Puoi utilizzare i dettagli nella schermata seguente per completare l\'operazione.',
    'bankTransfer.instructions':
        'Grazie per il tuo acquisto, utilizza i seguenti dati per completare il pagamento.',
    'bankTransfer.shopperEmail.contextualText':
        'Una copia dei dettagli del pagamento verrà inviata alla tua e-mail, così potrai completare il pagamento in un secondo momento.',
    'bankTransfer.details.title': 'Dettagli di pagamento',
    'bankTransfer.instruction.title': 'Processo di pagamento',
    'bankTransfer.instruction.step1': "Apri l'app della tua banca",
    'bankTransfer.instruction.step2': 'Avvia un nuovo trasferimento',
    'bankTransfer.instruction.step3': 'Immetti i dettagli del conto forniti di seguito',
    'bankTransfer.instruction.step4': 'Rivedi e conferma il pagamento',
    'bacs.accountHolderName': 'Nome del titolare del conto bancario',
    'bacs.accountHolderName.invalid': 'Nome del titolare del conto bancario non valido',
    'bacs.accountNumber': 'Numero di conto bancario',
    'bacs.accountNumber.invalid': 'Numero di conto bancario non valido',
    'bacs.bankLocationId': 'Sort code',
    'bacs.bankLocationId.invalid': 'Sort code non valido',
    'bacs.consent.amount':
        "Accetto che l'importo sopra indicato venga detratto dal mio conto bancario.",
    'bacs.consent.account':
        "Confermo che il conto è intestato al sottoscritto e che sono l'unico firmatario a dover autorizzare l'addebito diretto su questo conto.",
    edit: 'Modifica',
    'bacs.confirm': 'Conferma e paga',
    'bacs.result.introduction': "Scarica le Istruzioni per l'addebito diretto (DDI / Mandato)",
    'download.pdf': 'Scarica PDF',
    'creditCard.encryptedCardNumber.aria.iframeTitle': 'Iframe per il numero di carta',
    'creditCard.encryptedExpiryDate.aria.iframeTitle': 'Iframe per data di scadenza',
    'creditCard.encryptedExpiryMonth.aria.iframeTitle': 'Iframe per il mese di scadenza',
    'creditCard.encryptedExpiryYear.aria.iframeTitle': "Iframe per l'anno di scadenza",
    'creditCard.encryptedSecurityCode.aria.iframeTitle': 'Iframe per il codice di sicurezza',
    'creditCard.encryptedPassword.aria.iframeTitle': 'Iframe per la password',
    'giftcard.encryptedCardNumber.aria.iframeTitle': 'Iframe per il numero di carta',
    'giftcard.encryptedSecurityCode.aria.iframeTitle': 'Iframe per il pin',
    giftcardTransactionLimit: 'Importo massimo di %{amount} per transazione su questo buono regalo',
    'ach.encryptedBankAccountNumber.aria.iframeTitle': 'Iframe per il numero di conto bancario',
    'ach.encryptedBankLocationId.aria.iframeTitle': 'Iframe per il numero di routing bancario',
    'pix.instructions':
        "Utilizza l'app della tua banca per scansionare il codice QR o copia il codice PIX qui sotto per completare il pagamento.",
    'pix.code.label': 'Codice PIX',
    'pix.code.copy.label': 'Copia il codice PIX',
    'pix.code.copied.label': 'Codice PIX copiato',
    'twint.saved': 'memorizzato',
    orPayWith: 'oppure paga con',
    'invalid.format.expects': '%{label} è un formato non valido. Formato previsto: %{format}',
    'upi.qrCodeWaitingMessage':
        'Per completare il pagamento, scansiona il codice QR utilizzando la tua app UPI preferita',
    'upi.vpaWaitingMessage': "Apri l'app UPI per confermare il pagamento",
    'upi.modeSelection': 'Come vuoi utilizzare UPI?',
    'upi.completePayment': 'Completa il pagamento',
    'upi.mode.enterUpiId': 'ID UPI',
    'upi.mode.qrCode': 'Codice QR',
    'upi.mode.payByAnyUpi': 'App UPI',
    'upi.collect.dropdown.label': "Immetti l'ID UPI",
    'upi.collect.instruction': 'Inserisci il tuo ID UPI',
    'upi.collect.field.label': 'ID UPI',
    'upi.collect.field.contextualText':
        "Inserisci l'ID UPI collegato al tuo account e premi Continua",
    'upi.collect.field.invalid-id-error': 'Inserisci un UPI valido',
    'upi.error.noAppSelected': 'Seleziona la tua app UPI preferita per continuare',
    'upi.intent.instruction': "Seleziona l'app UPI che preferisci",
    'upi.qrCode.instruction':
        'Dopo aver generato il codice QR, puoi utilizzare la tua app UPI preferita per completare il pagamento.',
    'onlineBanking.termsAndConditions': 'Facendo clic, accetti i %#termini e le condizioni%#',
    'onlineBankingPL.termsAndConditions':
        "Continuando accetti il %#regolamento%# e l'%#obbligo di informativa%# di Przelewy24",
    'ctp.loading.poweredByCtp': 'Realizzato da Click to Pay',
    'ctp.loading.intro': 'Stiamo verificando se hai delle carte salvate con Click to Pay...',
    'ctp.login.title': 'Continua su Click to Pay',
    'ctp.login.subtitle': "Per continuare, inserisci l'indirizzo e-mail collegato a Click to Pay.",
    'ctp.login.inputLabel': 'E-mail',
    'ctp.logout.notYou': 'Non sei tu?',
    'ctp.logout.notYourCards': 'Non sono le tue carte?',
    'ctp.logout.notYourCard': 'Non è la tua carta?',
    'ctp.logout.notYourProfile': 'Non è il tuo profilo?',
    'ctp.otp.fieldLabel': 'Codice monouso',
    'ctp.otp.resendCode': 'Invia nuovamente il codice',
    'ctp.otp.codeResent': 'Codice inviato',
    'ctp.otp.title': 'Accedi alle tue carte Click to Pay',
    'ctp.otp.subtitle':
        'Inserisci il codice che %@ ha inviato a %@ per verificare la tua identità.',
    'ctp.otp.saveCookiesCheckbox.label': 'Salta la verifica la prossima volta',
    'ctp.otp.saveCookiesCheckbox.information':
        'Scegli di essere ricordato sul dispositivo e il browser che utilizzi per velocizzare il pagamento nei negozi partecipanti. Non consigliato per dispositivi condivisi.',
    'ctp.otp.saveCookiesCheckbox.shorterInfo':
        'Seleziona per essere ricordato sul tuo dispositivo e sul browser',
    'ctp.emptyProfile.message': 'Non esistono carte registrate in questo profilo Click to Pay',
    'ctp.separatorText': 'o utilizza',
    'ctp.cards.title': 'Completa il pagamento con Click to Pay',
    'ctp.cards.subtitle': 'Seleziona una carta da utilizzare.',
    'ctp.cards.expiredCard': 'Scaduta',
    'ctp.manualCardEntry': 'Inserimento manuale della carta',
    'ctp.aria.infoModalButton': "Che cos'è Click to Pay",
    'ctp.infoPopup.title': 'Click to Pay offre la facilità del contactless online',
    'ctp.infoPopup.subtitle':
        'Un metodo di pagamento veloce e sicuro supportato da Mastercard, Visa e altre carte di pagamento.',
    'ctp.infoPopup.benefit1':
        'Click to Pay si avvale della crittografia per garantire la sicurezza delle tue informazioni',
    'ctp.infoPopup.benefit2': 'Usa questo strumento con i commercianti di tutto il mondo',
    'ctp.infoPopup.benefit3':
        'Effettua una sola configurazione per avere la possibilità di effettuare pagamenti più agevoli in futuro',
    'ctp.errors.AUTH_INVALID': 'Autenticazione non valida',
    'ctp.errors.NOT_FOUND':
        "Nessun account trovato. Inserisci un'e-mail valida o procedi con l'inserimento manuale della carta.",
    'ctp.errors.ID_FORMAT_UNSUPPORTED': 'Formato non supportato',
    'ctp.errors.FRAUD': "L'account utente è stato bloccato o disattivato",
    'ctp.errors.CONSUMER_ID_MISSING': "L'identità del consumatore non è presente nella richiesta",
    'ctp.errors.ACCT_INACCESSIBLE':
        'Questo account non è attualmente disponibile, ad esempio perché è bloccato',
    'ctp.errors.CODE_INVALID': 'Codice di verifica non corretto',
    'ctp.errors.CODE_EXPIRED': 'Questo codice è scaduto',
    'ctp.errors.RETRIES_EXCEEDED':
        'È stato superato il limite di tentativi per la generazione del codice OTP',
    'ctp.errors.OTP_SEND_FAILED': 'Impossibile inviare il codice OTP al destinatario',
    'ctp.errors.REQUEST_TIMEOUT':
        'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'ctp.errors.UNKNOWN_ERROR':
        'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'ctp.errors.SERVICE_ERROR':
        'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'ctp.errors.SERVER_ERROR':
        'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'ctp.errors.INVALID_PARAMETER':
        'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'ctp.errors.AUTH_ERROR': 'Si è verificato un errore. Riprova o inserisci manualmente la carta',
    'paymentMethodsList.aria.label': 'Scegli un metodo di pagamento',
    'paymentMethodsList.storedPayments.label': 'Metodi di pagamento salvati',
    'paymentMethodsList.otherPayments.label': 'Altre opzioni di pagamento',
    'paymentMethodsList.otherPayments.buttonLabel': 'Mostra %{count} in più',
    'paymentMethodsList.promotedPayments.label': 'altro',
    'companyDetails.name.invalid': "Immetti il nome dell'azienda",
    'companyDetails.registrationNumber.invalid': 'Immetti il numero di registrazione',
    'consent.checkbox.invalid': 'È necessario accettare i termini e le condizioni',
    'trustly.descriptor': 'Pagamento bancario istantaneo',
    'trustly.description1':
        'Paga direttamente da uno qualsiasi dei tuoi conti bancari, garantito da una sicurezza di livello bancario',
    'trustly.description2': 'Nessuna carta, nessun download di app, nessuna registrazione',
    'ancv.input.label': 'Il tuo identificativo ANCV',
    'ancv.confirmPayment': 'Utilizza la tua richiesta ANCV per confermare il pagamento.',
    'ancv.form.instruction':
        'Per confermare il pagamento è necessario utilizzare la richiesta Cheque-Vacances.',
    'ancv.beneficiaryId.invalid': 'Inserisci un indirizzo e-mail o un identificativo ANCV valido',
    'creditCard.cardNumber.label': 'Numero carta',
    'creditCard.expiryDate.label': 'Data di scadenza',
    'creditCard.expiryDate.contextualText': 'Parte anteriore della carta in formato MM/YY',
    'creditCard.securityCode.label': 'Codice di sicurezza',
    'creditCard.securityCode.label.optional': 'Codice di sicurezza (facoltativo)',
    'creditCard.securityCode.contextualText.3digits': '3 cifre sul retro della carta',
    'creditCard.securityCode.contextualText.4digits': '4 cifre sulla parte anteriore della carta',
    'creditCard.expiryMonth.label': 'Mese di scadenza',
    'creditCard.expiryYear.label': 'Anno di scadenza',
    'creditCard.pin.label': 'Pin',
    'creditCard.password.label': 'Prime 2 cifre della password della carta',
    'giftcard.cardNumber.label': 'Numero carta',
    'giftcard.expiryDate.label': 'Data di scadenza',
    'giftcard.securityCode.label': 'Pin',
    'ach.bankAccountNumber.label': 'Numero di conto',
    'ach.bankLocationId.label': 'Codice ABA',
    'dragonpayVoucher.selectField.contextualText.nonBank': 'Seleziona il tuo fornitore',
    'dragonpayVoucher.selectField.contextualText.bank': 'Seleziona la banca',
    'payme.openPayMeApp':
        "Completa il pagamento nell'app PayMe concedendo l'autorizzazione e attendendo il messaggio di conferma.",
    'payme.redirectButtonLabel': "Apri l'app PayMe",
    'payme.scanQrCode': 'Completa il pagamento con il codice QR',
    'payme.timeToPay': 'Questo codice QR è valido per %@',
    'payme.instructions.steps':
        "Apri l'app PayMe.%@Scansiona il codice QR per autorizzare il pagamento.%@Completa il pagamento nell'app e attendi la conferma.",
    'payme.instructions.footnote':
        'Rimani su questa pagina fino a quando il pagamento sarà completato',
    'payByBankAISDD.disclaimer.header':
        'Utilizza Pay by Bank per pagare istantaneamente da qualsiasi conto bancario.',
    'payByBankAISDD.disclaimer.body':
        "Collegando il tuo conto bancario, autorizzi l'addebito su di esso di qualsiasi importo dovuto per l'utilizzo dei nostri servizi e/o l'acquisto dei nostri prodotti, fino alla revoca di tale autorizzazione.",
    'paymentMethodBrand.other': 'altro',
    'paynow.scanQrCode':
        "Per completare il pagamento, scansiona il codice QR utilizzando l'app PayNow",
    'paynow.mobileViewInstruction.step1': 'Fai uno screenshot del codice QR.',
    'paynow.mobileViewInstruction.step2': "Apri PayNow o l'app di pagamento.",
    'paynow.mobileViewInstruction.step3': "Seleziona l'opzione per scansionare un codice QR.",
    'paynow.mobileViewInstruction.step4':
        "Scegli l'opzione per caricare un QR e seleziona lo screenshot.",
    'paynow.mobileViewInstruction.step5': 'Completa la transazione.',
    'payto.payid.description':
        'Immettere la PayID e i dettagli del conto collegati al conto PayTo.',
    'payto.payid.label.identifier': 'Identificatore',
    'payto.payid.label.orgid': 'ID organizzazione',
    'payto.payid.option.phone': 'Cellulare',
    'payto.payid.option.email': 'E-mail',
    'payto.payid.option.orgid': "ID dell'organizzazione",
    'payto.bsb.description':
        'Per continuare, immettere il numero di conto bancario e la filiale della banca collegata al tuo conto',
    'payto.bsb.label.bankAccountNumber': 'Numero di conto bancario',
    'payto.instructions.steps':
        "Accedere all'app bancaria PayTo@Autorizzare il contratto PayTo@Approvare i termini di pagamento@Completare la transazione",
    'payto.confirmPayment':
        "Grazie per l'acquisto, completare il pagamento seguendo le istruzioni riportate di seguito.",
    'payto.mandate.amount.label': 'Somma',
    'payto.mandate.frequency.label': 'Frequenza',
    'payto.mandate.remarks.label': 'Descrizione',
    'payto.mandate.startsAt.label': 'Data del primo pagamento',
    'payto.mandate.endsAt.label': 'Data di fine',
    'payto.mandate.payee.label': 'Beneficiario',
    'payto.mandate.amount.max': 'Fino a %{amount} per transazione',
    'payto.mandate.frequency.adhoc-no-count': 'Ad Hoc',
    'payto.mandate.frequency.adhoc': '%{count} volta(e)',
    'payto.mandate.frequency.daily': '%{count} pagamento(i) giornaliero(i)',
    'payto.mandate.frequency.weekly': '%{count} pagamento(i) settimanale(i)',
    'payto.mandate.frequency.biWeekly': '%{count} pagamento/i ogni quindici giorni',
    'payto.mandate.frequency.monthly': '%{count} pagamento/i mensile(i)',
    'payto.mandate.frequency.quarterly': '%{count} pagamento/i trimestrale(i)',
    'payto.mandate.frequency.halfYearly': '%{count} pagamento(i) semestrale(i)',
    'payto.mandate.frequency.yearly': "%{count} pagamento/i all'anno",
    'payto.label.firstName': 'Nome del titolare del conto',
    'payto.label.lastName': 'Cognome del titolare del conto',
    'payto.await.waitForConfirmation': 'In attesa della tua approvazione',
    'payto.bsb.option.label': 'BSB e numero di conto',
    'card.fastlane.a11y.logo': 'Logo Fastlane by Paypal',
    'card.fastlane.a11y.openDialog': 'Legga di più su Fastlane',
    'card.fastlane.a11y.closeDialog': 'Chiudi la finestra di dialogo',
    'card.fastlane.mobileInputLabel': 'Numero di cellulare',
    'card.fastlane.consentToggle':
        'Salva le informazioni con Fastlane per velocizzare i pagamenti e altro',
    'card.fastlane.consentText':
        "Con il salvataggio delle tue informazioni, accetti di ricevere codici via SMS per usare Fastlane ovunque sia disponibile. Fastlane invierà un SMS al numero fornito. Accetti anche questi %#termini%# e l'%#informativa sulla privacy%#.",
    'card.fastlane.modal.benefit1.header': 'Compilazione automatica dei checkout',
    'card.fastlane.modal.benefit1.text':
        'Ottieni codici monouso per utilizzare le carte e gli indirizzi che hai salvato.',
    'card.fastlane.modal.benefit2.header': 'Proteggi i tuoi dati',
    'card.fastlane.modal.benefit2.text':
        'Le tue informazioni di pagamento vengono crittografate quando vengono archiviate e inviate ai luoghi in cui fai acquisti.',
    'card.fastlane.modal.benefit3.header': 'Utilizza in tutti i negozi',
    'card.fastlane.modal.benefit3.text':
        'Accelera il processo di pagamento ovunque sia disponibile Fastlane.',
    'paybybankpix.redirectBtn.label': "Continua su PIX attraverso l'OpenBanking",
    'paybybankpix.issuerList.disclaimer':
        "Continuando, accetti l'%#Informativa sul consenso%# e verrai reindirizzato alla tua banca.",
    'paybybankpix.issuerList.selectField.label': 'Banca',
    'paybybankpix.issuerList.introduction.logo.content':
        'Collega le chiavi PIX preferite per pagare tramite Open Finance',
    'paybybankpix.await.waitForConfirmation': 'In attesa della tua conferma...',
    'paybybankpix.await.timeToPay': 'Questo processo richiederà circa %{numberOfMin} minuti',
    'paybybankpix.await.withOpenFinance': 'Con la facilità di Open Finance',
    'paybybankpix.await.logoAlt.openFinance': 'Open Finance',
    'paybybankpix.storedPayment.payButton.label': 'Procedi al pagamento',
    'paybybankpix.storedPayment.additionalLabel': 'Conto %{receiver} tramite Pix',
    'paybybankpix.await.fetchDetails': 'Recupero dettagli...',
    'eftpad-canada.input.accountHolderName.label': 'Nome del titolare del conto',
    'eftpad-canada.input.accountHolderName.error': 'Inserisci il nome del titolare del conto',
    'eftpad-canada.input.accountNumber.label': 'Numero di conto',
    'eftpad-canada.input.accountNumber.contextualText': 'Tra 7 e 12 cifre',
    'eftpad-canada.input.accountNumber.error':
        'Inserisci un numero di conto compreso tra 7 e 12 cifre',
    'eftpad-canada.input.institutionNumber.label': "Numero dell'istituzione",
    'eftpad-canada.input.institutionNumber.contextualText': '3 cifre',
    'eftpad-canada.input.institutionNumber.error':
        'Inserisci un numero di istituto che consiste di 3 cifre',
    'eftpad-canada.input.transitNumber.label': 'Numero di transito',
    'eftpad-canada.input.transitNumber.contextualText': '5 cifre',
    'eftpad-canada.input.transitNumber.error':
        'Inserisci un numero di transito composto da 5 cifre',
    'eftpad-canada.settlement-info':
        'I fondi verranno prelevati dal conto bancario entro 1–3 giorni lavorativi.',
    'upi.mandate.intro': 'Stai impostando un pagamento ricorrente UPI Autopay',
    'upi.mandate.max.extraText':
        'Approverai un limite più alto per consentire future modifiche al piano.',
    'upi.mandate.upTo': 'fino a %{amount}',
    'upi.mandate.frequency.monthly': '/mese',
    'upi.mandate.frequency.weekly': '/settimana',
    'upi.mandate.frequency.adhoc': ' come presentato'
}
const adyenNl = {
    'creditCard.numberField.title': 'Kaartnummer',
    'creditCard.expiryDateField.title': 'Vervaldatum',
    'creditCard.cvcField.title': 'Beveiligingscode',
    'creditCard.cvcField.placeholder.3digits': '3 cijfers',
    'creditCard.expiryDateField.placeholder': 'MM/JJ',
    'error.va.sf-cc-num.01': 'Voer een geldig kaartnummer in',
    'error.va.sf-cc-num.02': 'Voer het kaartnummer in',
    'error.va.sf-cc-num.03': 'Voer een ondersteund kaartmerk in',
    'error.va.sf-cc-num.04': 'Voer het volledige kaartnummer in',
    'error.va.sf-cc-dat.01': 'Voer een geldige vervaldatum in',
    'error.va.sf-cc-dat.02': 'Voer een geldige vervaldatum in',
    'error.va.sf-cc-dat.03': 'Creditcard vervalt bijna',
    'error.va.sf-cc-dat.04': 'Voer de vervaldatum in',
    'error.va.sf-cc-dat.05': 'Voer de volledige vervaldatum in',
    'error.va.sf-cc-mth.01': 'Voer de vervalmaand in',
    'error.va.sf-cc-yr.01': 'Voer het vervaljaar in',
    'error.va.sf-cc-yr.02': 'Voer het volledige vervaljaar in',
    'error.va.sf-cc-cvc.01': 'Voer de beveiligingscode in',
    'error.va.sf-cc-cvc.02': 'Voer de volledige beveiligingscode in',
    payButton: 'Betaal',
    'payButton.redirecting': 'U wordt doorverwezen...',
    'payButton.with': 'Betaal %{value} met %{maskedData}',
    'payButton.saveDetails': 'Gegevens opslaan',
    close: 'Sluiten',
    storeDetails: 'Bewaar voor mijn volgende betaling',
    readMore: 'Verder lezen',
    'creditCard.holderName': 'Naam op kaart',
    'creditCard.holderName.invalid': 'Voer naam in zoals weergegeven op kaart',
    'creditCard.expiryDateField.month': 'Maand',
    'creditCard.expiryDateField.year': 'Jaar',
    'creditCard.storeDetailsButton': 'Onthouden voor de volgende keer',
    'creditCard.dualBrand.title': 'Merk van kaart',
    'creditCard.dualBrand.description':
        'Selecteer het merk van de kaart waarmee u het liefst wilt betalen. Dit is optioneel.',
    installments: 'Aantal termijnen',
    installmentOption: '%{times}x %{partialValue}',
    installmentOptionMonths: '%{times} maanden',
    'installments.oneTime': 'Eenmalige betaling',
    'installments.installments': 'Betaling termijnen',
    'installments.revolving': 'Terugkerende betaling',
    'sepaDirectDebit.ibanField.invalid': 'Ongeldig rekeningnummer',
    'sepa.ownerName': 'Ten name van',
    'sepa.ibanNumber': 'Rekeningnummer (IBAN)',
    'error.title': 'Fout',
    'error.subtitle.redirect': 'Doorsturen niet gelukt',
    'error.subtitle.payment': 'Betaling is niet geslaagd',
    'error.subtitle.refused': 'Betaling geweigerd',
    'error.message.unknown': 'Er is een onbekende fout opgetreden',
    'errorPanel.title': 'Bestaande fouten',
    'idealIssuer.selectField.title': 'Bank',
    'creditCard.success': 'Betaling geslaagd',
    loading: 'Laden...',
    continue: 'Doorgaan',
    continueTo: 'Doorgaan naar',
    'wechatpay.timetopay': 'U heeft %@ om te betalen',
    'sr.wechatpay.timetopay': 'U hebt %#minuten%# %#seconden%# om te betalen',
    'wechatpay.scanqrcode': 'QR-code scannen',
    personalDetails: 'Persoonlijke gegevens',
    companyDetails: 'Bedrijfsgegevens',
    'companyDetails.name': 'Bedrijfsnaam',
    'companyDetails.registrationNumber': 'Registratienummer',
    socialSecurityNumber: 'Burgerservicenummer',
    firstName: 'Voornaam',
    'firstName.invalid': 'Voer je voornaam in',
    infix: 'Voorvoegsel',
    lastName: 'Achternaam',
    'lastName.invalid': 'Voer je achternaam in',
    mobileNumber: 'Telefoonnummer mobiel',
    'mobileNumber.invalid': 'Ongeldig mobiel nummer',
    city: 'Stad',
    postalCode: 'Postcode',
    'postalCode.optional': 'Postcode (optioneel)',
    countryCode: 'Landcode',
    telephoneNumber: 'Telefoonnummer',
    telephonePrefix: 'Voorvoegsel',
    dateOfBirth: 'Geboortedatum',
    shopperEmail: 'E-mailadres',
    gender: 'Geslacht',
    'gender.notselected': 'Selecteer uw geslacht',
    male: 'Man',
    female: 'Vrouw',
    billingAddress: 'Factuuradres',
    street: 'Straatnaam',
    stateOrProvince: 'Staat of provincie',
    country: 'Land/regio',
    houseNumberOrName: 'Huisnummer',
    separateDeliveryAddress: 'Een afwijkend bezorgadres opgeven',
    deliveryAddress: 'Bezorgadres',
    'deliveryAddress.firstName': 'Voornaam ontvanger',
    'deliveryAddress.lastName': 'Achternaam ontvanger',
    zipCode: 'Postcode',
    apartmentSuite: 'Appartement/Suite',
    provinceOrTerritory: 'Provincie of territorium',
    cityTown: 'Stad',
    address: 'Adres',
    'address.search.contextualText': 'Typ om te beginnen met het zoeken naar uw adres',
    'address.errors.incomplete': 'Voer een adres in om verder te gaan',
    'address.enterManually': 'Voer het adres handmatig in',
    state: 'Staat',
    'field.title.optional': '(optioneel)',
    'issuerList.selectField.contextualText': 'Selecteer uw bank om door te gaan',
    'issuerList.selectField.label': 'Bankselectie',
    privacyPolicy: 'Privacybeleid',
    'afterPay.agreement': 'Ik ga akkoord met de %@ van Riverty',
    'riverty.termsAndConditions':
        'Ik ga akkoord met de %#algemene voorwaarden%# voor de betalingsmethode Riverty. Het privacybeleid van Riverty vindt u %#hier%#.',
    paymentConditions: 'betalingsvoorwaarden',
    openApp: 'Open de app',
    'voucher.readInstructions': 'Instructies lezen',
    'voucher.introduction':
        'Bedankt voor uw aankoop. Gebruik deze coupon om uw betaling te voltooien.',
    'voucher.expirationDate': 'Vervaldatum',
    'voucher.alternativeReference': 'Alternatieve referentie',
    'voucher.paymentReferenceLabel': 'Betalingsreferentie',
    'voucher.surcharge': 'Inclusief %@ toeslag',
    'voucher.introduction.doku':
        'Bedankt voor uw aankoop. Gebruik de volgende informatie om uw betaling te voltooien.',
    'voucher.shopperName': 'Klantnaam',
    'voucher.merchantName': 'Verkoper',
    'voucher.introduction.econtext':
        'Bedankt voor uw aankoop. Gebruik de volgende informatie om uw betaling te voltooien.',
    'voucher.telephoneNumber': 'Telefoonnummer',
    'voucher.shopperReference': 'Klant referentie',
    'voucher.collectionInstitutionNumber': 'Nummer ophaallocatie',
    'voucher.econtext.telephoneNumber.invalid':
        'Het telefoonnummer moet uit 10 of 11 cijfers bestaan',
    'boletobancario.btnLabel': 'Boleto genereren',
    'boleto.sendCopyToEmail': 'Stuur een kopie naar mijn e-mailadres',
    'button.copy': 'Kopiëren',
    'button.copied': 'Gekopieerd!',
    'button.download': 'Downloaden',
    'boleto.socialSecurityNumber': 'CPF/CNPJ',
    'boleto.socialSecurityNumber.invalid': 'Voer een geldig CPF/CNPJ-nummer in',
    'creditCard.storedCard.description.ariaLabel': 'Opgeslagen kaart eindigt op %@',
    'voucher.entity': 'Entiteit',
    donateButton: 'Doneren',
    donationTermsCondition: 'Door te doneren gaat u akkoord met de %#algemene voorwaarden%#',
    donationRoundUpDescription:
        '%{donationAmount} is een afronding ten opzichte van uw oorspronkelijke betaling (%{originalPaymentAmount})',
    notNowButton: 'Niet nu',
    thanksForYourSupport: 'Bedankt voor uw donatie!',
    'resultMessages.preauthorized': 'Gegevens opgeslagen',
    preauthorizeWith: 'Preautorisatie uitvoeren met',
    confirmPreauthorization: 'Preautorisatie bevestigen',
    confirmPurchase: 'Aankoop bevestigen',
    applyGiftcard: 'Inwisselen',
    giftcardBalance: 'Saldo cadeaukaart',
    deductedBalance: 'Afgetrokken bedrag',
    'creditCard.taxNumber': 'Geboortedatum of bedrijfsregistratienummer van kaarthouder',
    'creditCard.taxNumber.label':
        'Geboortedatum (JJ-MM-DD) of bedrijfsregistratienummer (10 cijfers) van kaarthouder',
    'creditCard.taxNumber.labelAlt': 'Bedrijfsregistratienummer (10 cijfers)',
    'creditCard.taxNumber.invalid':
        'Geboortedatum of bedrijfsregistratienummer van kaarthouder is ongeldig',
    'storedPaymentMethod.disable.button': 'Verwijderen',
    'storedPaymentMethod.disable.confirmation': 'Opgeslagen betalingsmethode verwijderen',
    'storedPaymentMethod.disable.confirmButton': 'Ja, verwijderen',
    'storedPaymentMethod.disable.cancelButton': 'Annuleren',
    'ach.bankAccount': 'Bankrekening',
    'ach.bankAccount.title': 'Bankrekeninggegevens',
    'ach.bankAccount.label': 'Rekeningtype',
    'ach.bankAccount.placeholder': 'Kies een rekeningtype',
    'ach.bankAccount.nothing-selected-error': 'Kies een rekeningtype',
    'ach.bankAccount.option.personal-checking': 'Persoonlijke betaalrekening',
    'ach.bankAccount.option.personal-savings': 'Persoonlijke spaarrekening',
    'ach.bankAccount.option.business-checking': 'Zakelijke betaalrekening',
    'ach.bankAccount.option.business-savings': 'Zakelijke spaarrekening',
    'ach.bankAccountNumberVerification.label': 'Rekeningnummer verifiëren',
    'ach.bankAccountNumberVerification.error.not-match': 'Rekeningnummer komt niet overeen',
    'ach.bankAccountNumberVerification.error.empty':
        'Voer het opgegeven rekeningnummer in om het nummer te controleren',
    'ach.routingNumber.label': 'Routingnummer',
    'ach.accountHolderNameField.title': 'Naam rekeninghouder',
    'ach.accountHolderNameField.contextualText': 'Staat op de voorkant van uw kaart',
    'ach.accountHolderNameField.invalid': 'Ongeldige naam rekeninghouder',
    'ach.accountNumberField.invalid': 'Ongeldig rekeningnummer',
    'ach.accountLocationField.invalid': 'Ongeldig routingnummer (ABA)',
    'ach.savedBankAccount': 'Opgeslagen bankrekening',
    'ach.savings': 'Spaarrekening',
    'ach.checking': 'Betaalrekening',
    'select.state': 'Selecteer staat',
    'select.stateOrProvince': 'Selecteer staat of provincie',
    'select.provinceOrTerritory': 'Selecteer provincie of territorium',
    'select.country': 'Selecteer land/regio',
    'select.noOptionsFound': 'Geen opties gevonden',
    'telephoneNumber.invalid': 'Ongeldig telefoonnummer',
    qrCodeOrApp: 'of',
    'paypal.processingPayment': 'Betaling wordt verwerkt...',
    generateQRCode: 'Genereer QR-code',
    'await.waitForConfirmation': 'Wacht op bevestiging',
    'mbway.confirmPayment': 'Bevestig uw betaling via de MB WAY-app',
    'shopperEmail.invalid': 'Ongeldig e-mailadres',
    'dateOfBirth.format': 'DD/MM/JJJJ',
    'dateOfBirth.invalid':
        'Voer een geldige geboortedatum in die aangeeft dat u ten minste 18 jaar oud bent',
    'blik.confirmPayment': 'Open uw bankapp om de betaling te bevestigen.',
    'blik.invalid': 'Voer 6 cijfers in',
    'blik.code': '6-cijferige code',
    'blik.help': 'Haal de code op in uw bankapp.',
    'swish.pendingMessage':
        'Nadat u hebt gescand, kan de status maximaal 10 minuten in behandeling zijn. Als u binnen deze periode opnieuw probeert te betalen, kunnen er meerdere keren kosten in rekening worden gebracht.',
    'field.valid': 'Geldig veld',
    'field.invalid': 'Veld niet geldig',
    'field.error.required': 'Voer het %{label} in',
    'field.error.invalid': 'Voer een geldig %{label} in',
    'error.giftcard.no-balance': 'Deze cadeaukaart heeft geen saldo',
    'error.giftcard.card-error': 'We hebben geen cadeaukaart met dit nummer in onze administratie',
    'error.giftcard.currency-error':
        'Cadeaukaarten zijn alleen geldig in de valuta waarin ze zijn uitgegeven',
    'err.gen.9100': 'Onvolledig veld',
    'err.gen.9101': 'Veld niet geldig',
    'err.gen.9102': 'Fout bij het parseren van gegevens',
    '3ds.chal.805': 'Uitdaging opgelost zonder een% {result} -object',
    'cc.num.902': 'Voer een geldig kaartnummer in',
    'cc.num.900': 'Voer het kaartnummer in',
    'cc.num.903': 'Voer een ondersteund kaartmerk in',
    'cc.num.901': 'Voer het volledige kaartnummer in',
    'cc.dat.912': 'Voer een geldige vervaldatum in',
    'cc.dat.913': 'Voer een geldige vervaldatum in',
    'cc.dat.914': 'Creditcard vervalt bijna',
    'cc.dat.910': 'Voer de vervaldatum in',
    'cc.dat.911': 'Voer de volledige vervaldatum in',
    'cc.mth.915': 'Voer de vervalmaand in',
    'cc.yr.917': 'Voer het vervaljaar in',
    'cc.yr.918': 'Voer het volledige vervaljaar in',
    'cc.cvc.920': 'Voer de beveiligingscode in',
    'cc.cvc.921': 'Voer de volledige beveiligingscode in',
    'ach.num.945': 'Voer het bankrekeningnummer in',
    'ach.num.946': 'Voer het volledige bankrekeningnummer in',
    'ach.loc.947': 'Voer het bankroutingnummer in',
    'ach.loc.948': 'Voer het volledige bankroutingnummer in',
    'kcp.pwd.940': 'Voer het wachtwoord in',
    'kcp.pwd.941': 'Voer het volledige wachtwoord in',
    'amazonpay.signout': 'Afmelden bij Amazon',
    'amazonpay.changePaymentDetails': 'Betalingsgegevens wijzigen',
    'partialPayment.warning':
        'Selecteer een andere betaalmethode om het resterende deel te betalen',
    'partialPayment.remainingBalance': 'Het resterende saldo is %{amount}',
    'bankTransfer.beneficiary': 'Naam van begunstigde',
    'bankTransfer.iban': 'IBAN',
    'bankTransfer.bic': 'BIC',
    'bankTransfer.reference': 'Referentie',
    'bankTransfer.accountNumber': 'Rekeningnummer',
    'bankTransfer.sortCode': 'Bankcode',
    'bankTransfer.routingNumber': 'Routingnummer',
    'bankTransfer.branchCode': 'Vestigingscode',
    'bankTransfer.bankCode': 'Bankcode',
    'bankTransfer.introduction':
        "Druk op 'Doorgaan' om een nieuwe overschrijving aan te maken. U kunt de gegevens in het volgende scherm gebruiken om deze betaling af te ronden.",
    'bankTransfer.instructions':
        'Bedankt voor uw aankoop. Gebruik de volgende informatie om uw betaling te voltooien.',
    'bankTransfer.shopperEmail.contextualText':
        'Een kopie van de transactiedetails wordt naar uw e-mailadres gestuurd, zodat u de betaling later kunt afronden.',
    'bankTransfer.details.title': 'Transactiedetails',
    'bankTransfer.instruction.title': 'Betalingsproces',
    'bankTransfer.instruction.step1': 'Open uw bankapp',
    'bankTransfer.instruction.step2': 'Wilt u een nieuwe overschrijving starten?',
    'bankTransfer.instruction.step3': 'Voer de onderstaande accountgegevens in',
    'bankTransfer.instruction.step4': 'Controleer en bevestig de betaling',
    'bacs.accountHolderName': 'Naam bankrekeninghouder',
    'bacs.accountHolderName.invalid': 'Ongeldige naam bankrekeninghouder',
    'bacs.accountNumber': 'Bankrekeningnummer',
    'bacs.accountNumber.invalid': 'Ongeldig bankrekeningnummer',
    'bacs.bankLocationId': 'Bankcode',
    'bacs.bankLocationId.invalid': 'Ongeldige bankcode',
    'bacs.consent.amount':
        'Ik ga ermee akkoord dat het bovengenoemde bedrag van mijn bankrekening wordt afgeschreven.',
    'bacs.consent.account':
        "Ik bevestig dat de rekening op mijn naam staat en dat ik de enige ondertekenaar ben die toestemming kan geven voor automatische incasso's vanaf deze rekening.",
    edit: 'Bewerken',
    'bacs.confirm': 'Bevestigen en betalen',
    'bacs.result.introduction': 'Download uw machtiging automatische incasso',
    'download.pdf': 'PDF downloaden',
    'creditCard.encryptedCardNumber.aria.iframeTitle': 'Iframe voor kaartnummer',
    'creditCard.encryptedExpiryDate.aria.iframeTitle': 'Iframe voor vervaldatum',
    'creditCard.encryptedExpiryMonth.aria.iframeTitle': 'Iframe voor vervalmaand',
    'creditCard.encryptedExpiryYear.aria.iframeTitle': 'Iframe voor vervaljaar',
    'creditCard.encryptedSecurityCode.aria.iframeTitle': 'Iframe voor beveiligingscode',
    'creditCard.encryptedPassword.aria.iframeTitle': 'Iframe voor wachtwoord',
    'giftcard.encryptedCardNumber.aria.iframeTitle': 'Iframe voor kaartnummer',
    'giftcard.encryptedSecurityCode.aria.iframeTitle': 'Iframe voor PIN',
    giftcardTransactionLimit: 'Max. %{amount} toegestaan per transactie met deze cadeaubon',
    'ach.encryptedBankAccountNumber.aria.iframeTitle': 'Iframe voor bankrekeningnummer',
    'ach.encryptedBankLocationId.aria.iframeTitle': 'Iframe voor bankroutingnummer',
    'pix.instructions':
        'Gebruik uw bank-app om de QR-code te scannen of kopieer de PIX-code hieronder om uw betaling af te ronden.',
    'pix.code.label': 'PIX-code',
    'pix.code.copy.label': 'PIX-code kopiëren',
    'pix.code.copied.label': 'PIX-code gekopieerd',
    'twint.saved': 'opgeslagen',
    orPayWith: 'of betaal met',
    'invalid.format.expects': '%{label} Ongeldige indeling. Verwachte indeling: %{format}',
    'upi.qrCodeWaitingMessage':
        'Scan de QR-code met je favoriete UPI-app om de betaling te voltooien',
    'upi.vpaWaitingMessage': 'Open je UPI-app om de betaling te bevestigen',
    'upi.modeSelection': 'Hoe wilt u UPI gebruiken?',
    'upi.completePayment': 'U betaling afronden',
    'upi.mode.enterUpiId': 'UPI-ID',
    'upi.mode.qrCode': 'QR-code',
    'upi.mode.payByAnyUpi': 'UPI-app',
    'upi.collect.dropdown.label': 'UPI-ID invoeren',
    'upi.collect.instruction': 'Voer uw UPI-ID in',
    'upi.collect.field.label': 'UPI-ID',
    'upi.collect.field.contextualText':
        'Voer de UPI-ID in die aan uw account is gekoppeld en druk op Doorgaan',
    'upi.collect.field.invalid-id-error': 'Voer een geldige UPI in',
    'upi.error.noAppSelected': 'Selecteer uw gewenste UPI-app om door te gaan.',
    'upi.intent.instruction': 'Selecteer uw gewenste UPI-app',
    'upi.qrCode.instruction':
        'Na het aanmaken van de QR-code kunt u de gewenste UPI-app gebruiken om de betaling te voltooien.',
    'onlineBanking.termsAndConditions':
        'Door verder te gaan gaat u akkoord met de %#algemene voorwaarden%#',
    'onlineBankingPL.termsAndConditions':
        'Door verder te gaan stemt u in met de %#richtlijnen%# en %#informatieverplichting%# van Przelewy24',
    'ctp.loading.poweredByCtp': 'Mogelijk gemaakt door Click to Pay',
    'ctp.loading.intro': 'We controleren of u opgeslagen kaarten heeft bij Click to Pay...',
    'ctp.login.title': 'Ga verder naar Click to Pay',
    'ctp.login.subtitle': 'Voer het aan Click to Pay gekoppelde e-mailadres in om verder te gaan.',
    'ctp.login.inputLabel': 'E-mail',
    'ctp.logout.notYou': 'Bent u dit niet?',
    'ctp.logout.notYourCards': 'Niet uw kaarten?',
    'ctp.logout.notYourCard': 'Niet uw kaart?',
    'ctp.logout.notYourProfile': 'Niet uw profiel?',
    'ctp.otp.fieldLabel': 'Eenmalige code',
    'ctp.otp.resendCode': 'Code opnieuw verzenden',
    'ctp.otp.codeResent': 'Code opnieuw verzonden',
    'ctp.otp.title': 'Ga naar uw Click to Pay-kaarten',
    'ctp.otp.subtitle':
        'Voer de code %@ in die naar %@ is gestuurd, zodat we kunnen verifiëren dat u het bent.',
    'ctp.otp.saveCookiesCheckbox.label': 'Verificatie de volgende keer overslaan',
    'ctp.otp.saveCookiesCheckbox.information':
        'Selecteer om te worden onthouden op uw apparaat en in uw browser bij deelnemende winkels, zodat u sneller kunt afrekenen. Niet aanbevolen voor gedeelde apparaten.',
    'ctp.otp.saveCookiesCheckbox.shorterInfo':
        'Selecteer om onthouden te worden op uw apparaat en browser',
    'ctp.emptyProfile.message': 'Geen kaarten geregistreerd voor dit Click to Pay-profiel',
    'ctp.separatorText': 'of gebruik',
    'ctp.cards.title': 'Voltooi de betaling met Click to Pay',
    'ctp.cards.subtitle': 'Selecteer een kaart om te gebruiken.',
    'ctp.cards.expiredCard': 'Verlopen',
    'ctp.manualCardEntry': 'Handmatige kaartinvoer',
    'ctp.aria.infoModalButton': 'Wat is Click to Pay?',
    'ctp.infoPopup.title': 'Met Click to Pay betaalt u nu ook contactloos online',
    'ctp.infoPopup.subtitle':
        'Een snelle en veilige betaalmethode, ondersteund door Mastercard, Visa en andere betaalkaarten.',
    'ctp.infoPopup.benefit1':
        'Click to Pay maakt gebruik van versleuteling om uw informatie veilig te houden',
    'ctp.infoPopup.benefit2': 'Gebruik het bij winkeliers over de hele wereld',
    'ctp.infoPopup.benefit3': 'Eén keer instellen voor probleemloze betalingen',
    'ctp.errors.AUTH_INVALID': 'Authenticatie ongeldig',
    'ctp.errors.NOT_FOUND':
        'Geen account gevonden, voer een geldig e-mailadres in of ga door met het handmatig invoeren van kaarten',
    'ctp.errors.ID_FORMAT_UNSUPPORTED': 'Formaat wordt niet ondersteund',
    'ctp.errors.FRAUD': 'Gebruikersaccount vergrendeld of uitgeschakeld',
    'ctp.errors.CONSUMER_ID_MISSING': 'Identiteit van de consument ontbreekt in het verzoek',
    'ctp.errors.ACCT_INACCESSIBLE':
        'Dit account is momenteel niet beschikbaar, omdat het bijvoorbeeld is vergrendeld',
    'ctp.errors.CODE_INVALID': 'Onjuiste verificatiecode',
    'ctp.errors.CODE_EXPIRED': 'Deze code is verlopen',
    'ctp.errors.RETRIES_EXCEEDED':
        'De limiet voor het aantal pogingen voor het genereren van een OTP is overschreden',
    'ctp.errors.OTP_SEND_FAILED': 'Het OTP kan niet naar de ontvanger worden verzonden',
    'ctp.errors.REQUEST_TIMEOUT':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'ctp.errors.UNKNOWN_ERROR':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'ctp.errors.SERVICE_ERROR':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'ctp.errors.SERVER_ERROR':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'ctp.errors.INVALID_PARAMETER':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'ctp.errors.AUTH_ERROR':
        'Er is iets fout gegaan, probeer het opnieuw of gebruik de handmatige kaartinvoer',
    'paymentMethodsList.aria.label': 'Kies een betaalmethode',
    'paymentMethodsList.storedPayments.label': 'Opgeslagen betaalmethoden',
    'paymentMethodsList.otherPayments.label': 'Meer betaalmogelijkheden',
    'paymentMethodsList.otherPayments.buttonLabel': 'Nog %{count} weergeven',
    'paymentMethodsList.promotedPayments.label': 'overige',
    'companyDetails.name.invalid': 'Voer de bedrijfsnaam in',
    'companyDetails.registrationNumber.invalid': 'Voer het registratienummer in',
    'consent.checkbox.invalid': 'Je moet akkoord gaan met de algemene voorwaarden',
    'form.instruction': 'Alle velden zijn verplicht, tenzij anders aangegeven.',
    'trustly.descriptor': 'Directe bankbetaling',
    'trustly.description1':
        'Rechtstreeks betalen vanaf een van uw bankrekeningen, ondersteund door beveiliging op bankniveau',
    'trustly.description2': 'Geen kaarten, geen app downloaden, geen registratie',
    'ancv.input.label': 'Uw ANCV-identificatie',
    'ancv.confirmPayment': 'Gebruik uw ANCV-toepassing om de betaling te bevestigen.',
    'ancv.form.instruction':
        'De Cheque-Vacances applicatie is nodig om deze betaling te valideren.',
    'ancv.beneficiaryId.invalid': 'Voer een geldig e-mailadres of ANCV-id in',
    'creditCard.cardNumber.label': 'Kaartnummer',
    'creditCard.expiryDate.label': 'Vervaldatum',
    'creditCard.expiryDate.contextualText': 'Voorkant van kaart in MM/YY-formaat',
    'creditCard.securityCode.label': 'Beveiligingscode',
    'creditCard.securityCode.label.optional': 'Beveiligingscode (optioneel)',
    'creditCard.securityCode.contextualText.3digits': '3 cijfers op de achterkant van de kaart',
    'creditCard.securityCode.contextualText.4digits': '4 cijfers op de voorkant van de kaart',
    'creditCard.expiryMonth.label': 'Vervalmaand',
    'creditCard.expiryYear.label': 'Vervaljaar',
    'creditCard.pin.label': 'PIN',
    'creditCard.password.label': 'Eerste twee cijfers van het wachtwoord van de kaart',
    'giftcard.cardNumber.label': 'Kaartnummer',
    'giftcard.expiryDate.label': 'Vervaldatum',
    'giftcard.securityCode.label': 'PIN',
    'ach.bankAccountNumber.label': 'Rekeningnummer',
    'ach.bankLocationId.label': 'Routingnummer (ABA)',
    'dragonpayVoucher.selectField.contextualText.nonBank': 'Selecteer uw aanbieder',
    'dragonpayVoucher.selectField.contextualText.bank': 'Selecteer uw bank',
    'payme.openPayMeApp':
        'Voltooi uw betaling door deze te autoriseren in de PayMe-app en de bevestiging af te wachten.',
    'payme.redirectButtonLabel': 'Open de PayMe-app',
    'payme.scanQrCode': 'Voltooi uw betaling met een QR-code',
    'payme.timeToPay': 'Deze QR-code is %@ geldig',
    'payme.instructions.steps':
        'Open de PayMe-app.%@Scan de QR-code om de betaling te autoriseren.%@Voltooi de betaling in de app en wacht de bevestiging af.',
    'payme.instructions.footnote': 'Sluit deze pagina niet voordat de betaling is voltooid',
    'payByBankAISDD.disclaimer.header':
        'Gebruik Pay by Bank om direct vanaf elke bankrekening te betalen.',
    'payByBankAISDD.disclaimer.body':
        'Door uw bankrekening te koppelen, machtigt u afschrijvingen van uw rekening voor elk bedrag dat u verschuldigd bent voor het gebruik van onze diensten en/of de aankoop van onze producten, totdat deze machtiging wordt ingetrokken.',
    'paymentMethodBrand.other': 'overige',
    'paynow.scanQrCode': 'Scan de QR-code met de PayNow-app om de betaling te voltooien',
    'paynow.mobileViewInstruction.step1': 'Maak een screenshot van de QR-code.',
    'paynow.mobileViewInstruction.step2': 'Open de bank- of betaalapp van PayNow.',
    'paynow.mobileViewInstruction.step3': 'Selecteer de optie om een QR-code te scannen.',
    'paynow.mobileViewInstruction.step4':
        'Kies de optie om een QR-code te uploaden en selecteer de schermafbeelding.',
    'paynow.mobileViewInstruction.step5': 'Voltooi de transactie.',
    'payto.payid.description':
        'Voer de PayID en accountgegevens in die gekoppeld zijn aan uw PayTo-account.',
    'payto.payid.label.identifier': 'Identificator',
    'payto.payid.label.orgid': 'Organisatie-ID',
    'payto.payid.option.phone': 'Mobiele telefoon',
    'payto.payid.option.email': 'E-mail',
    'payto.payid.option.orgid': 'Organisatie-ID',
    'payto.bsb.description':
        'Voer het bankrekeningnummer en BSB in die aan uw rekening zijn gekoppeld om door te gaan',
    'payto.bsb.label.bankAccountNumber': 'Bankrekeningnummer',
    'payto.instructions.steps':
        'Log in op uw PayTo-bankapp@Autoriseer de PayTo-overeenkomst@Keur de betalingsvoorwaarden goed@Voltooi de transactie',
    'payto.confirmPayment':
        'Bedankt voor uw aankoop. Volg de onderstaande instructies om uw betaling te voltooien.',
    'payto.mandate.amount.label': 'Bedrag',
    'payto.mandate.frequency.label': 'Frequentie',
    'payto.mandate.remarks.label': 'Beschrijving',
    'payto.mandate.startsAt.label': 'Eerste betalingsdatum',
    'payto.mandate.endsAt.label': 'Einddatum',
    'payto.mandate.payee.label': 'Begunstigde',
    'payto.mandate.amount.max': 'Tot %{amount} per transactie toegestaan',
    'payto.mandate.frequency.adhoc-no-count': 'Ad hoc',
    'payto.mandate.frequency.adhoc': '%{count} keer',
    'payto.mandate.frequency.daily': '%{count} betaling(en) per dag',
    'payto.mandate.frequency.weekly': '%{count} betaling(en) per week',
    'payto.mandate.frequency.biWeekly': '%{count} betaling(en) om de twee weken',
    'payto.mandate.frequency.monthly': '%{count} betaling(en) per maand',
    'payto.mandate.frequency.quarterly': '%{count} betaling(en) per kwartaal',
    'payto.mandate.frequency.halfYearly': '%{count} betaling(en) per half jaar',
    'payto.mandate.frequency.yearly': '%{count} betaling(en) per jaar',
    'payto.label.firstName': 'Voornaam rekeninghouder',
    'payto.label.lastName': 'Achternaam rekeninghouder',
    'payto.await.waitForConfirmation': 'In afwachting van uw goedkeuring',
    'payto.bsb.option.label': 'BSB- en rekeningnummer',
    'card.fastlane.a11y.logo': 'Logo Fastlane by Paypal',
    'card.fastlane.a11y.openDialog': 'Lees meer over Fastlane',
    'card.fastlane.a11y.closeDialog': 'Dialoogvenster sluiten',
    'card.fastlane.mobileInputLabel': 'Telefoonnummer mobiel',
    'card.fastlane.consentToggle':
        'Bewaar uw gegevens bij Fastlane om sneller af te rekenen en meer',
    'card.fastlane.consentText':
        'Door uw gegevens op te slaan, gaat u ermee akkoord codes per sms te ontvangen om Fastlane overal te gebruiken waar dit beschikbaar is. Fastlane stuurt een sms naar het opgegeven nummer. U gaat ook akkoord met deze %#voorwaarden%# en deze %#privacyverklaring%#.',
    'card.fastlane.modal.benefit1.header': 'Gegevens automatisch invullen bij het afrekenen',
    'card.fastlane.modal.benefit1.text':
        'Ontvang eenmalige codes om uw opgeslagen kaarten en adressen te gebruiken.',
    'card.fastlane.modal.benefit2.header': 'Bescherm uw gegevens',
    'card.fastlane.modal.benefit2.text':
        'Uw betalingsgegevens worden versleuteld opgeslagen en verzonden naar locaties waar u aankopen doet.',
    'card.fastlane.modal.benefit3.header': 'Te gebruiken in meerdere winkels',
    'card.fastlane.modal.benefit3.text':
        'Snel afrekenen op alle locaties waar Fastlane beschikbaar is.',
    'paybybankpix.redirectBtn.label': 'Doorgaan naar PIX via OpenBanking',
    'paybybankpix.issuerList.disclaimer':
        'Door verder te gaan, gaat u akkoord met de %#Toestemmingsverklaring%# en wordt u doorgestuurd naar uw bank.',
    'paybybankpix.issuerList.selectField.label': 'Bank',
    'paybybankpix.issuerList.introduction.logo.content':
        'Koppel uw favoriete PIX-sleutels om via Open Finance te betalen.',
    'paybybankpix.await.waitForConfirmation': 'Wachten op uw bevestiging...',
    'paybybankpix.await.timeToPay': 'Dit proces zal ongeveer %{numberOfMin} minuut(en) duren',
    'paybybankpix.await.withOpenFinance': 'Met het gemak van Open Finance',
    'paybybankpix.await.logoAlt.openFinance': 'Open Finance',
    'paybybankpix.storedPayment.payButton.label': 'Betaling voortzetten',
    'paybybankpix.storedPayment.additionalLabel': '%{receiver}-account via PIX',
    'paybybankpix.await.fetchDetails': 'Gegevens ophalen...',
    'eftpad-canada.input.accountHolderName.label': 'Naam rekeninghouder',
    'eftpad-canada.input.accountHolderName.error': 'Voer de naam van een bankrekeninghouder in',
    'eftpad-canada.input.accountNumber.label': 'Rekeningnummer',
    'eftpad-canada.input.accountNumber.contextualText': 'Tussen 7 en 12 cijfers',
    'eftpad-canada.input.accountNumber.error':
        'Voer een rekeningnummer in dat tussen de 7 en 12 cijfers lang is',
    'eftpad-canada.input.institutionNumber.label': 'Instellingsnummer',
    'eftpad-canada.input.institutionNumber.contextualText': '3 cijfers',
    'eftpad-canada.input.institutionNumber.error':
        'Voer een instellingsnummer in dat uit 3 cijfers bestaat',
    'eftpad-canada.input.transitNumber.label': 'Transitnummer',
    'eftpad-canada.input.transitNumber.contextualText': '5 cijfers',
    'eftpad-canada.input.transitNumber.error':
        'Voer een transitnummer in dat uit 5 cijfers bestaat',
    'eftpad-canada.settlement-info':
        'De fondsen worden binnen 1–3 werkdagen van uw bankrekening afgeschreven.',
    'upi.mandate.intro': 'U stelt een terugkerende UPI Autopay-betaling in',
    'upi.mandate.max.extraText':
        'U keurt een hogere limiet goed om toekomstige abonnementswijzigingen toe te staan',
    'upi.mandate.upTo': 'tot %{bedrag}',
    'upi.mandate.frequency.monthly': 'per maand',
    'upi.mandate.frequency.weekly': 'per week',
    'upi.mandate.frequency.adhoc': ' op verzoek'
}
const adyenDe = {
    'creditCard.numberField.title': 'Kartennummer',
    'creditCard.expiryDateField.title': 'Ablaufdatum',
    'creditCard.cvcField.title': 'Sicherheitscode',
    'creditCard.cvcField.placeholder.3digits': '3 Stellen',
    'creditCard.expiryDateField.placeholder': 'MM/JJ',
    'error.va.sf-cc-num.01': 'Geben Sie eine gültige Kartennummer ein',
    'error.va.sf-cc-num.02': 'Geben Sie die Kartennummer ein',
    'error.va.sf-cc-num.03': 'Geben Sie eine unterstützte Kartenmarke ein',
    'error.va.sf-cc-num.04': 'Geben Sie die vollständige Kartennummer ein',
    'error.va.sf-cc-dat.01': 'Geben Sie ein gültiges Ablaufdatum ein',
    'error.va.sf-cc-dat.02': 'Geben Sie ein gültiges Ablaufdatum ein',
    'error.va.sf-cc-dat.03': 'Kreditkarte läuft bald ab',
    'error.va.sf-cc-dat.04': 'Geben Sie das Ablaufdatum ein',
    'error.va.sf-cc-dat.05': 'Geben Sie das vollständige Ablaufdatum ein',
    'error.va.sf-cc-mth.01': 'Geben Sie den Ablaufmonat ein',
    'error.va.sf-cc-yr.01': 'Geben Sie das Ablaufjahr ein',
    'error.va.sf-cc-yr.02': 'Geben Sie das vollständige Ablaufjahr ein',
    'error.va.sf-cc-cvc.01': 'Geben Sie den Sicherheitscode ein',
    'error.va.sf-cc-cvc.02': 'Geben Sie den vollständigen Sicherheitscode ein',
    payButton: 'Zahle',
    'payButton.redirecting': 'Umleiten…',
    'payButton.with': '%{value} mit %{maskedData} zahlen',
    'payButton.saveDetails': 'Angaben speichern',
    close: 'Schließen',
    storeDetails: 'Für zukünftige Zahlvorgänge speichern',
    readMore: 'Mehr lesen',
    'creditCard.holderName': 'Name auf der Karte',
    'creditCard.holderName.invalid': 'Geben Sie den Namen wie auf der Karte gezeigt ein',
    'creditCard.expiryDateField.month': 'Monat',
    'creditCard.expiryDateField.year': 'Jahr',
    'creditCard.storeDetailsButton': 'Für das nächste Mal speichern',
    'creditCard.dualBrand.title': 'Kartenmarke',
    'creditCard.dualBrand.description':
        'Wählen Sie die Kartenmarke aus, mit der Sie bezahlen möchten. Dies ist optional.',
    installments: 'Anzahl der Raten',
    installmentOption: '%{times}x %{partialValue}',
    installmentOptionMonths: '%{times} Monate',
    'installments.oneTime': 'Einmalige Zahlung',
    'installments.installments': 'Ratenzahlung',
    'installments.revolving': 'Ratenzahlung',
    'sepaDirectDebit.ibanField.invalid': 'Ungültige Kontonummer',
    'sepa.ownerName': 'Name des Kontoinhabers',
    'sepa.ibanNumber': 'Kontonummer (IBAN)',
    'error.title': 'Fehler',
    'error.subtitle.redirect': 'Weiterleitung fehlgeschlagen',
    'error.subtitle.payment': 'Zahlung fehlgeschlagen',
    'error.subtitle.refused': 'Zahlvorgang verweigert',
    'error.message.unknown': 'Es ist ein unbekannter Fehler aufgetreten.',
    'errorPanel.title': 'Bestehende Fehler',
    'idealIssuer.selectField.title': 'Bank',
    'creditCard.success': 'Zahlung erfolgreich',
    loading: 'Laden…',
    continue: 'Weiter',
    continueTo: 'Weiter zu',
    'wechatpay.timetopay': 'Sie haben noch %@ um zu zahlen',
    'sr.wechatpay.timetopay': 'Sie haben %#Minuten%# %#Sekunden%# Zeit, um zu bezahlen',
    'wechatpay.scanqrcode': 'QR-Code scannen',
    personalDetails: 'Persönliche Angaben',
    companyDetails: 'Unternehmensdaten',
    'companyDetails.name': 'Unternehmensname',
    'companyDetails.registrationNumber': 'Registrierungsnummer',
    socialSecurityNumber: 'Sozialversicherungsnummer',
    firstName: 'Vorname',
    'firstName.invalid': 'Geben Sie Ihren Vornamen ein',
    infix: 'Vorwahl',
    lastName: 'Nachname',
    'lastName.invalid': 'Geben Sie Ihren Nachnamen ein',
    mobileNumber: 'Handynummer',
    'mobileNumber.invalid': 'Ungültige Handynummer',
    city: 'Stadt',
    postalCode: 'Postleitzahl',
    'postalCode.optional': 'Postleitzahl (optional)',
    countryCode: 'Landesvorwahl',
    telephoneNumber: 'Telefonnummer',
    telephonePrefix: 'Vorwahl',
    dateOfBirth: 'Geburtsdatum',
    shopperEmail: 'E-Mail-Adresse',
    gender: 'Geschlecht',
    'gender.notselected': 'Wählen Sie Ihr Geschlecht',
    male: 'Männlich',
    female: 'Weiblich',
    billingAddress: 'Rechnungsadresse',
    street: 'Straße',
    stateOrProvince: 'Bundesland',
    country: 'Land/Region',
    houseNumberOrName: 'Hausnummer',
    separateDeliveryAddress: 'Abweichende Lieferadresse angeben',
    deliveryAddress: 'Lieferadresse',
    'deliveryAddress.firstName': 'Vorname des Empfängers',
    'deliveryAddress.lastName': 'Nachname des Empfängers',
    zipCode: 'PLZ',
    apartmentSuite: 'Wohnung/Geschoss',
    provinceOrTerritory: 'Provinz oder Territorium',
    cityTown: 'Ort',
    address: 'Straße und Hausnummer',
    'address.search.contextualText': 'Tippen Sie, um die Suche nach Ihrer Adresse zu starten',
    'address.errors.incomplete': 'Geben Sie eine Adresse ein, um fortzufahren',
    'address.enterManually': 'Geben Sie die Adresse manuell ein',
    state: 'Bundesstaat',
    'field.title.optional': '(optional)',
    'issuerList.selectField.contextualText': 'Wählen Sie Ihre Bank aus, um fortzufahren',
    'issuerList.selectField.label': 'Auswahl der Bank',
    privacyPolicy: 'Datenschutz',
    'afterPay.agreement': 'Ich stimme den %@ von Riverty zu',
    'riverty.termsAndConditions':
        'Ich bin mit den allgemeinen %#Geschäftsbedingungen%# für die Zahlungsmethode Riverty einverstanden. Die Datenschutzerklärung von Riverty finden Sie %#hier%#.',
    paymentConditions: 'Zahlungsbedingungen',
    openApp: 'App öffnen',
    'voucher.readInstructions': 'Anweisungen lesen',
    'voucher.introduction':
        'Vielen Dank für Ihren Kauf. Bitte schließen Sie Ihre Zahlung unter Verwendung des folgenden Gutscheins ab.',
    'voucher.expirationDate': 'Gültig bis',
    'voucher.alternativeReference': 'Alternative Referenz',
    'voucher.paymentReferenceLabel': 'Zahlungsreferenz',
    'voucher.surcharge': 'Inkl. % @Zuschlag',
    'voucher.introduction.doku':
        'Vielen Dank für Ihren Kauf. Bitte schließen Sie Ihre Zahlung unter Verwendung der folgenden Informationen ab.',
    'voucher.shopperName': 'Name des Käufers',
    'voucher.merchantName': 'Händler',
    'voucher.introduction.econtext':
        'Vielen Dank für Ihren Kauf. Bitte schließen Sie Ihre Zahlung unter Verwendung der folgenden Informationen ab.',
    'voucher.telephoneNumber': 'Telefonnummer',
    'voucher.shopperReference': 'Kundenreferenz',
    'voucher.collectionInstitutionNumber': 'Nummer der Zahlungsannahmestelle',
    'voucher.econtext.telephoneNumber.invalid': 'Die Telefonnummer muss 10- oder 11-stellig sein',
    'boletobancario.btnLabel': 'Boleto generieren',
    'boleto.sendCopyToEmail': 'Eine Kopie an meine E-Mail-Adresse senden',
    'button.copy': 'Kopieren',
    'button.copied': 'Kopiert!',
    'button.download': 'Herunterladen',
    'boleto.socialSecurityNumber': 'CPF/CNPJ',
    'boleto.socialSecurityNumber.invalid': 'Geben Sie eine gültige CPF-/CNPJ-Nummer ein',
    'creditCard.storedCard.description.ariaLabel': 'Gespeicherte Karte endet auf %@',
    'voucher.entity': 'Entität',
    donateButton: 'Spenden',
    donationTermsCondition:
        'Durch Ihre Spende stimmen Sie den %#Allgemeinen Geschäftsbedingungen%# zu',
    donationRoundUpDescription:
        '%{donationAmount} ist eine Aufrundung von Ihrer ursprünglichen Zahlung (%{originalPaymentAmount})',
    notNowButton: 'Nicht jetzt',
    thanksForYourSupport: 'Danke für Ihre Unterstützung!',
    'resultMessages.preauthorized': 'Angaben gespeichert',
    preauthorizeWith: 'Vorautorisieren mit',
    confirmPreauthorization: 'Vorautorisierung bestätigen',
    confirmPurchase: 'Kauf bestätigen',
    applyGiftcard: 'Einlösen',
    giftcardBalance: 'Saldo der Geschenkkarte',
    deductedBalance: 'Abgezogener Betrag',
    'creditCard.taxNumber':
        'Geburtsdatum des Karteninhabers oder der Unternehmensregistrierungsnummer',
    'creditCard.taxNumber.label':
        'Geburtsdatum des Karteninhabers (JJMMTT) oder Unternehmensregistrierungsnummer (10-stellig)',
    'creditCard.taxNumber.labelAlt': 'Unternehmensregistrierungsnummer (10-stellig)',
    'creditCard.taxNumber.invalid':
        'Ungültiges Geburtsdatum des Karteninhabers oder ungültige Unternehmensregistrierungsnummer',
    'storedPaymentMethod.disable.button': 'Entfernen',
    'storedPaymentMethod.disable.confirmation': 'Gespeicherte Zahlungsmethode entfernen',
    'storedPaymentMethod.disable.confirmButton': 'Ja, entfernen',
    'storedPaymentMethod.disable.cancelButton': 'Abbrechen',
    'ach.bankAccount': 'Bankkonto',
    'ach.bankAccount.title': 'Bankkontodetails',
    'ach.bankAccount.label': 'Kontotyp',
    'ach.bankAccount.placeholder': 'Wählen Sie einen Kontotyp aus',
    'ach.bankAccount.nothing-selected-error': 'Wählen Sie einen Kontotyp aus',
    'ach.bankAccount.option.personal-checking': 'Persönliches Girokonto',
    'ach.bankAccount.option.personal-savings': 'Persönliches Sparkonto',
    'ach.bankAccount.option.business-checking': 'Geschäftsgirokonto',
    'ach.bankAccount.option.business-savings': 'Geschäftssparkonto',
    'ach.bankAccountNumberVerification.label': 'Kontonummer bestätigen',
    'ach.bankAccountNumberVerification.error.not-match': 'Kontonummer stimmt nicht überein',
    'ach.bankAccountNumberVerification.error.empty':
        'Geben Sie die angegebene Kontonummer ein, um die Kontonummer zu bestätigen',
    'ach.routingNumber.label': 'Bankleitzahl',
    'ach.accountHolderNameField.title': 'Name des Kontoinhabers',
    'ach.accountHolderNameField.contextualText': 'Befindet sich auf der Vorderseite Ihrer Karte',
    'ach.accountHolderNameField.invalid': 'Ungültiger Kontoinhabername',
    'ach.accountNumberField.invalid': 'Ungültige Kontonummer',
    'ach.accountLocationField.invalid': 'Ungültige ABA-Nummer',
    'ach.savedBankAccount': 'Gespeichertes Bankkonto',
    'ach.savings': 'Sparkonto',
    'ach.checking': 'Girokonto',
    'select.state': 'Bundesstaat auswählen',
    'select.stateOrProvince': 'Bundesland oder Provinz/Region auswählen',
    'select.provinceOrTerritory': 'Provinz oder Territorium auswählen',
    'select.country': 'Land/Region auswählen',
    'select.noOptionsFound': 'Keine Optionen gefunden',
    'telephoneNumber.invalid': 'Ungültige Telefonnummer',
    qrCodeOrApp: 'oder',
    'paypal.processingPayment': 'Zahlung wird verarbeitet…',
    generateQRCode: 'QR-Code generieren',
    'await.waitForConfirmation': 'Warten auf Bestätigung',
    'mbway.confirmPayment': 'Bestätigen Sie Ihre Zahlung in der MB WAY-App',
    'shopperEmail.invalid': 'Ungültige E-Mail-Adresse',
    'dateOfBirth.format': 'TT.MM.JJJJ',
    'dateOfBirth.invalid':
        'Geben Sie ein gültiges Geburtsdatum ein, das angibt, dass Sie mindestens 18 Jahre alt sind.',
    'blik.confirmPayment': 'Öffnen Sie Ihre Banking-App, um die Zahlung zu bestätigen.',
    'blik.invalid': '6 Zahlen eingeben',
    'blik.code': '6-stelliger Code',
    'blik.help': 'Rufen Sie den Code über Ihre Banking-App ab.',
    'swish.pendingMessage':
        'Es kann sein, dass der Status bis zu 10 Minuten nach dem Scan „ausstehend“ lautet. Wenn Sie währenddessen einen neuen Zahlungsversuch unternehmen, kann es sein, dass Ihnen mehrere Beträge in Rechnung gestellt werden.',
    'field.valid': 'Feld gültig',
    'field.invalid': 'Feld ungültig',
    'field.error.required': 'Geben Sie %{label} ein',
    'field.error.invalid': 'Geben Sie einen gültigen %{label} ein',
    'error.giftcard.no-balance': 'Auf dieser Geschenkkarte ist kein Guthaben vorhanden',
    'error.giftcard.card-error': 'Es gibt in unserem System keine Geschenkkarte mit dieser Nummer',
    'error.giftcard.currency-error':
        'Geschenkkarten sind nur in der Währung gültig, in der sie ausgestellt wurden',
    'err.gen.9100': 'Feld nicht ausgefüllt',
    'err.gen.9101': 'Feld ungültig',
    'err.gen.9102': 'Fehler beim Parsen von Daten',
    '3ds.chal.805': 'Prozess ohne Objekt %{result} beendet',
    'cc.num.902': 'Geben Sie eine gültige Kartennummer ein',
    'cc.num.900': 'Geben Sie die Kartennummer ein',
    'cc.num.903': 'Geben Sie eine unterstützte Kartenmarke ein',
    'cc.num.901': 'Geben Sie die vollständige Kartennummer ein',
    'cc.dat.912': 'Geben Sie ein gültiges Ablaufdatum ein',
    'cc.dat.913': 'Geben Sie ein gültiges Ablaufdatum ein',
    'cc.dat.914': 'Kreditkarte läuft bald ab',
    'cc.dat.910': 'Geben Sie das Ablaufdatum ein',
    'cc.dat.911': 'Geben Sie das vollständige Ablaufdatum ein',
    'cc.mth.915': 'Geben Sie den Ablaufmonat ein',
    'cc.yr.917': 'Geben Sie das Ablaufjahr ein',
    'cc.yr.918': 'Geben Sie das vollständige Ablaufjahr ein',
    'cc.cvc.920': 'Geben Sie den Sicherheitscode ein',
    'cc.cvc.921': 'Geben Sie den vollständigen Sicherheitscode ein',
    'ach.num.945': 'Geben Sie die Kontonummer ein',
    'ach.num.946': 'Geben Sie die vollständige Kontonummer ein',
    'ach.loc.947': 'Geben Sie die Bankleitzahl ein',
    'ach.loc.948': 'Geben Sie die vollständige Bankleitzahl ein',
    'kcp.pwd.940': 'Geben Sie das Passwort ein',
    'kcp.pwd.941': 'Geben Sie das vollständige Passwort ein',
    'amazonpay.signout': 'Von Amazon abmelden',
    'amazonpay.changePaymentDetails': 'Zahlungsinformationen ändern',
    'partialPayment.warning': 'Wählen Sie eine andere Zahlungsmethode zur Zahlung des Restbetrags',
    'partialPayment.remainingBalance': 'Es verbleibt ein Restbetrag von %{amount}',
    'bankTransfer.beneficiary': 'Name des Begünstigten',
    'bankTransfer.iban': 'IBAN',
    'bankTransfer.bic': 'BIC',
    'bankTransfer.reference': 'Referenz',
    'bankTransfer.accountNumber': 'Kontonummer',
    'bankTransfer.sortCode': 'Bankleitzahl',
    'bankTransfer.routingNumber': 'Bankleitzahl',
    'bankTransfer.branchCode': 'Filialcode',
    'bankTransfer.bankCode': 'Bankleitzahl',
    'bankTransfer.introduction':
        'Drücken Sie auf „Weiter“, um eine neue Banküberweisungszahlung zu erstellen. Sie können die Informationen auf dem nächsten Bildschirm verwenden, um diese Zahlung abzuschließen.',
    'bankTransfer.instructions':
        'Vielen Dank für Ihren Kauf. Bitte schließen Sie Ihre Zahlung unter Verwendung der folgenden Informationen ab.',
    'bankTransfer.shopperEmail.contextualText':
        'Eine Kopie der Zahlungsinformationen wird an Ihre E-Mail-Adresse gesendet, damit Sie die Zahlung später abschließen können.',
    'bankTransfer.details.title': 'Zahlungsinformationen',
    'bankTransfer.instruction.title': 'Zahlungsprozess',
    'bankTransfer.instruction.step1': 'Öffnen Sie Ihre Banking-App',
    'bankTransfer.instruction.step2': 'Starten Sie eine neue Überweisung',
    'bankTransfer.instruction.step3': 'Geben Sie die unten angegebenen Kontodaten ein',
    'bankTransfer.instruction.step4': 'Überprüfen und bestätigen Sie die Zahlung',
    'bacs.accountHolderName': 'Name des Bankkontoinhabers',
    'bacs.accountHolderName.invalid': 'Ungültiger Bankkontoinhabername',
    'bacs.accountNumber': 'Bankkontonummer',
    'bacs.accountNumber.invalid': 'Ungültige Bankkontonummer',
    'bacs.bankLocationId': 'Bankleitzahl',
    'bacs.bankLocationId.invalid': 'Ungültige Bankleitzahl',
    'bacs.consent.amount':
        'Ich bin damit einverstanden, dass der oben genannte Betrag von meinem Bankkonto abgebucht wird.',
    'bacs.consent.account':
        'Ich bestätige, dass das Konto unter meinem Namen läuft und ich der einzige erforderliche Unterzeichner bin, um die Lastschrift für dieses Konto zu autorisieren.',
    edit: 'Bearbeiten',
    'bacs.confirm': 'Bestätigen und bezahlen',
    'bacs.result.introduction':
        'Laden Sie Ihre Lastschriftanweisung (DDI/Einzugsermächtigung) herunter',
    'download.pdf': 'PDF herunterladen',
    'creditCard.encryptedCardNumber.aria.iframeTitle': 'Iframe für Kartennummer',
    'creditCard.encryptedExpiryDate.aria.iframeTitle': 'Iframe für Ablaufdatum',
    'creditCard.encryptedExpiryMonth.aria.iframeTitle': 'Iframe für Ablaufmonat',
    'creditCard.encryptedExpiryYear.aria.iframeTitle': 'Iframe für Ablaufjahr',
    'creditCard.encryptedSecurityCode.aria.iframeTitle': 'Iframe für Sicherheitscode',
    'creditCard.encryptedPassword.aria.iframeTitle': 'Iframe für Passwort',
    'giftcard.encryptedCardNumber.aria.iframeTitle': 'Iframe für Kartennummer',
    'giftcard.encryptedSecurityCode.aria.iframeTitle': 'Iframe für Stift',
    giftcardTransactionLimit:
        'Der zulässige Höchstbetrag pro Transaktion für diese Geschenkkarte ist %{amount}',
    'ach.encryptedBankAccountNumber.aria.iframeTitle': 'Iframe für Bankkontonummer',
    'ach.encryptedBankLocationId.aria.iframeTitle': 'Iframe für Bankleitzahl',
    'pix.instructions':
        'Verwenden Sie Ihre Banking-App, um den QR-Code zu scannen, oder kopieren Sie den untenstehenden PIX-Code, um Ihre Zahlung abzuschließen.',
    'pix.code.label': 'PIX-Code',
    'pix.code.copy.label': 'PIX-Code kopieren',
    'pix.code.copied.label': 'PIX-Code wurde kopiert',
    'twint.saved': 'hinterlegt',
    orPayWith: 'oder bezahlen Sie mit',
    'invalid.format.expects': '%{label} Ungültiges Format. Erwartetes Format: %{format}',
    'upi.qrCodeWaitingMessage':
        'Scannen Sie den QR-Code mit Ihrer bevorzugten UPI-App, um die Zahlung abzuschließen.',
    'upi.vpaWaitingMessage': 'Öffnen Sie Ihre UPI-App, um die Zahlung zu bestätigen.',
    'upi.modeSelection': 'Wie möchten Sie UPI verwenden?',
    'upi.completePayment': 'Schließen Sie Ihre Zahlung ab',
    'upi.mode.enterUpiId': 'UPI-ID',
    'upi.mode.qrCode': 'QR-Code',
    'upi.mode.payByAnyUpi': 'UPI-App',
    'upi.collect.dropdown.label': 'UPI-ID eingeben',
    'upi.collect.instruction': 'Geben Sie Ihre UPI-ID ein',
    'upi.collect.field.label': 'UPI-ID',
    'upi.collect.field.contextualText':
        'Geben Sie die mit Ihrem Konto verknüpfte UPI-ID ein und drücken Sie auf „Weiter“.',
    'upi.collect.field.invalid-id-error': 'Geben Sie eine gültige UPI ein',
    'upi.error.noAppSelected': 'Wählen Sie Ihre bevorzugte UPI-App aus, um fortzufahren',
    'upi.intent.instruction': 'Wählen Sie Ihre bevorzugte UPI-App',
    'upi.qrCode.instruction':
        'Nach der Generierung des QR-Codes können Sie Ihre bevorzugte UPI-App verwenden, um die Zahlung abzuschließen.',
    'onlineBanking.termsAndConditions':
        'Wenn Sie fortfahren, stimmen Sie den %#Allgemeinen Geschäftsbedingungen%# zu',
    'onlineBankingPL.termsAndConditions':
        'Indem Sie fortfahren, stimmen Sie den %#Vorschriften%# und der %#Auskunftspflicht%# von Przelewy24 zu',
    'ctp.loading.poweredByCtp': 'Unterstützt von Click to Pay',
    'ctp.loading.intro':
        'Wir überprüfen gerade, ob Sie bereits gespeicherte Click-to-Pay-Karten haben…',
    'ctp.login.title': 'Weiter zu Click to Pay',
    'ctp.login.subtitle':
        'Geben Sie die mit Click to Pay verbundene E-Mail-Adresse ein, um fortzufahren.',
    'ctp.login.inputLabel': 'E-Mail-Adresse',
    'ctp.logout.notYou': 'Sind das nicht Sie?',
    'ctp.logout.notYourCards': 'Sind das nicht Ihre Karten?',
    'ctp.logout.notYourCard': 'Das ist nicht Ihre Karte?',
    'ctp.logout.notYourProfile': 'Das ist nicht Ihr Profil?',
    'ctp.otp.fieldLabel': 'Einmaliger Code',
    'ctp.otp.resendCode': 'Code erneut senden',
    'ctp.otp.codeResent': 'Code erneut gesendet',
    'ctp.otp.title': 'Zugriff auf Ihre Click-to-Pay-Karten',
    'ctp.otp.subtitle':
        'Geben Sie den Code ein, der von %@ an %@ gesendet wurde, um Ihre Identität zu bestätigen.',
    'ctp.otp.saveCookiesCheckbox.label': 'Verifizierung beim nächsten Mal überspringen',
    'ctp.otp.saveCookiesCheckbox.information':
        'Wählen Sie diese Option, um bei teilnehmenden Geschäften auf Ihrem Gerät und Browser gespeichert zu werden, um den Bestellvorgang zu beschleunigen. Nicht für gemeinsam genutzte Geräte empfohlen.',
    'ctp.otp.saveCookiesCheckbox.shorterInfo':
        'Wählen Sie diese Option aus, um auf Ihrem Gerät und Browser gespeichert zu werden',
    'ctp.emptyProfile.message': 'In diesem Click-to-Pay-Profil sind keine Karten registriert',
    'ctp.separatorText': 'oder verwenden',
    'ctp.cards.title': 'Zahlung mit Click to Pay abschließen',
    'ctp.cards.subtitle': 'Wählen Sie die Karte aus, die Sie verwenden möchten.',
    'ctp.cards.expiredCard': 'Abgelaufen',
    'ctp.manualCardEntry': 'Manuelle Karteneingabe',
    'ctp.aria.infoModalButton': 'Was ist Click to Pay?',
    'ctp.infoPopup.title': 'Click to Pay ist so einfach wie kontaktloses Bezahlen, nur online',
    'ctp.infoPopup.subtitle':
        'Eine schnelle, sichere Zahlungsmethode mit Mastercard, Visa und anderen Zahlungskarten.',
    'ctp.infoPopup.benefit1': 'Click to Pay nutzt Verschlüsselung zum Schutz Ihrer Daten',
    'ctp.infoPopup.benefit2': 'Verfügbar bei Händlern auf der ganzen Welt',
    'ctp.infoPopup.benefit3':
        'Nach einmaliger Einrichtung bezahlen Sie in Zukunft mit nur einem Klick.',
    'ctp.errors.AUTH_INVALID': 'Authentifizierung ungültig',
    'ctp.errors.NOT_FOUND':
        'Kein Konto gefunden, geben Sie eine gültige E-Mail-Adresse ein oder fahren Sie mit der manuellen Karteneingabe fort.',
    'ctp.errors.ID_FORMAT_UNSUPPORTED': 'Format wird nicht unterstützt',
    'ctp.errors.FRAUD': 'Das Benutzerkonto wurde gesperrt oder deaktiviert',
    'ctp.errors.CONSUMER_ID_MISSING': 'Die Identität des Verbrauchers fehlt in der Anfrage',
    'ctp.errors.ACCT_INACCESSIBLE':
        'Dieses Konto ist derzeit nicht verfügbar; es kann z. B. gesperrt sein.',
    'ctp.errors.CODE_INVALID': 'Falscher Verifizierungscode',
    'ctp.errors.CODE_EXPIRED': 'Dieser Code ist abgelaufen',
    'ctp.errors.RETRIES_EXCEEDED':
        'Das Limit für die Anzahl der Wiederholungen für das Generieren von Einmalpasswörtern (OTPs) wurde überschritten',
    'ctp.errors.OTP_SEND_FAILED':
        'Das Einmalpasswort (OTP) konnte nicht an den Empfänger gesendet werden',
    'ctp.errors.REQUEST_TIMEOUT':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'ctp.errors.UNKNOWN_ERROR':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'ctp.errors.SERVICE_ERROR':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'ctp.errors.SERVER_ERROR':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'ctp.errors.INVALID_PARAMETER':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'ctp.errors.AUTH_ERROR':
        'Ein Fehler ist aufgetreten, versuchen Sie es erneut oder nutzen Sie die manuelle Karteneingabe.',
    'paymentMethodsList.aria.label': 'Wählen Sie eine Zahlungsmethode aus',
    'paymentMethodsList.storedPayments.label': 'Gespeicherte Zahlungsmethoden',
    'paymentMethodsList.otherPayments.label': 'Weitere Zahlungsmethoden',
    'paymentMethodsList.otherPayments.buttonLabel': '%{count} weitere anzeigen',
    'paymentMethodsList.promotedPayments.label': 'andere',
    'companyDetails.name.invalid': 'Geben Sie den Firmennamen ein',
    'companyDetails.registrationNumber.invalid': 'Geben Sie die Registrierungsnummer ein',
    'consent.checkbox.invalid': 'Sie müssen den Geschäftsbedingungen zustimmen',
    'form.instruction': 'Alle Felder sind Pflichtfelder, sofern nicht anders gekennzeichnet.',
    'trustly.descriptor': 'Sofortige Bankzahlung',
    'trustly.description1':
        'Bezahlen Sie direkt von einem Ihrer Bankkonten, abgesichert durch Sicherheiten auf Bankebene',
    'trustly.description2': 'Keine Karten, kein App-Download, keine Registrierung',
    'ancv.input.label': 'Ihre ANCV-Identifikation',
    'ancv.confirmPayment': 'Bestätigen Sie die Zahlung mit Ihrem ANCV-Antrag.',
    'ancv.form.instruction':
        'Zur Validierung dieser Zahlung ist der Antrag „Cheque-Vacances“ erforderlich.',
    'ancv.beneficiaryId.invalid': 'Geben Sie eine gültige E-Mail-Adresse oder ANCV-ID ein',
    'creditCard.cardNumber.label': 'Kartennummer',
    'creditCard.expiryDate.label': 'Ablaufdatum',
    'creditCard.expiryDate.contextualText': 'Vorderseite der Karte im Format MM/JJ',
    'creditCard.securityCode.label': 'Sicherheitscode',
    'creditCard.securityCode.label.optional': 'Sicherheitscode (optional)',
    'creditCard.securityCode.contextualText.3digits': '3 Ziffern auf der Rückseite der Karte',
    'creditCard.securityCode.contextualText.4digits': '4 Ziffern auf der Vorderseite der Karte',
    'creditCard.expiryMonth.label': 'Monat des Ablaufdatums',
    'creditCard.expiryYear.label': 'Jahr des Ablaufdatums',
    'creditCard.pin.label': 'PIN',
    'creditCard.password.label': 'Die ersten zwei Ziffern des Kartenpassworts',
    'giftcard.cardNumber.label': 'Kartennummer',
    'giftcard.expiryDate.label': 'Ablaufdatum',
    'giftcard.securityCode.label': 'PIN',
    'ach.bankAccountNumber.label': 'Kontonummer',
    'ach.bankLocationId.label': 'ABA-Nummer',
    'dragonpayVoucher.selectField.contextualText.nonBank': 'Anbieter auswählen',
    'dragonpayVoucher.selectField.contextualText.bank': 'Bank auswählen',
    'payme.openPayMeApp':
        'Schließen Sie Ihre Zahlung in der PayMe-App ab, indem Sie die Zahlung in der App autorisieren und auf die Bestätigung warten.',
    'payme.redirectButtonLabel': 'Öffnen Sie die PayMe-App',
    'payme.scanQrCode': 'Schließen Sie Ihre Zahlung per QR-Code ab',
    'payme.timeToPay': 'Dieser QR-Code gilt für %@',
    'payme.instructions.steps':
        'Öffnen Sie die PayMe-App.%@Scannen Sie den QR-Code, um die Zahlung zu autorisieren.%@Schließen Sie die Zahlung in der App ab und warten Sie auf eine Bestätigung.',
    'payme.instructions.footnote':
        'Bitte schließen Sie diese Seite nicht, bevor die Zahlung abgeschlossen ist',
    'payByBankAISDD.disclaimer.header':
        'Verwenden Sie Pay by Bank, um sofort von jedem Bankkonto zu bezahlen.',
    'payByBankAISDD.disclaimer.body':
        'Durch die Verbindung Ihres Bankkontos autorisieren Sie bis auf Widerruf Abbuchungen von Ihrem Konto für alle Beträge, die für die Nutzung unserer Dienste und/oder den Kauf unserer Produkte geschuldet werden.',
    'paymentMethodBrand.other': 'andere',
    'paynow.scanQrCode':
        'Scannen Sie den QR-Code mit der PayNow-App, um die Zahlung abzuschließen.',
    'paynow.mobileViewInstruction.step1': 'Machen Sie einen Screenshot des QR-Codes.',
    'paynow.mobileViewInstruction.step2': 'Öffnen Sie die PayNow-Bank- oder Zahlungs-App.',
    'paynow.mobileViewInstruction.step3': 'Wählen Sie die Option, um einen QR-Code zu scannen.',
    'paynow.mobileViewInstruction.step4':
        'Wählen Sie die Option zum Hochladen eines QR-Codes und wählen Sie den Screenshot aus.',
    'paynow.mobileViewInstruction.step5': 'Schließen Sie die Transaktion ab.',
    'payto.payid.description':
        'Geben Sie die PayID und die Kontodaten ein, die mit Ihrem PayTo-Konto verknüpft sind.',
    'payto.payid.label.identifier': 'Kennung',
    'payto.payid.label.orgid': 'Organisations-ID',
    'payto.payid.option.phone': 'Mobil',
    'payto.payid.option.email': 'E-Mail-Adresse',
    'payto.payid.option.orgid': 'Organisations-ID',
    'payto.bsb.description':
        'Geben Sie die Bankkontonummer und die dazugehörige Bankleitzahl ein, um fortzufahren',
    'payto.bsb.label.bankAccountNumber': 'Bankkontonummer',
    'payto.instructions.steps':
        'Melden Sie sich bei Ihrer PayTo-Banking-App an@Genehmigen Sie die PayTo-Vereinbarung@Stimmen Sie den Zahlungsbedingungen zu@Schließen Sie die Transaktion ab',
    'payto.confirmPayment':
        'Vielen Dank für Ihren Kauf. Bitte schließen Sie Ihre Zahlung ab, indem Sie die nachstehenden Anweisungen befolgen.',
    'payto.mandate.amount.label': 'Betrag',
    'payto.mandate.frequency.label': 'Häufigkeit',
    'payto.mandate.remarks.label': 'Beschreibung',
    'payto.mandate.startsAt.label': 'Erstes Zahlungsdatum',
    'payto.mandate.endsAt.label': 'Enddatum',
    'payto.mandate.payee.label': 'Zahlungsempfänger',
    'payto.mandate.amount.max': 'Bis zu %{amount} pro Transaktion',
    'payto.mandate.frequency.adhoc-no-count': 'Ad Hoc',
    'payto.mandate.frequency.adhoc': '%{count} Mal',
    'payto.mandate.frequency.daily': '%{count} Zahlung(en) täglich',
    'payto.mandate.frequency.weekly': '%{count} Zahlung(en) wöchentlich',
    'payto.mandate.frequency.biWeekly': '%{count} Zahlung(en) vierzehntägig',
    'payto.mandate.frequency.monthly': '%{count} Zahlung(en) monatlich',
    'payto.mandate.frequency.quarterly': '%{count} Zahlung(en) vierteljährlich',
    'payto.mandate.frequency.halfYearly': '%{count} Zahlung(en) halbjährlich',
    'payto.mandate.frequency.yearly': '%{count} Zahlung(en) jährlich',
    'payto.label.firstName': 'Vorname des Kontoinhabers',
    'payto.label.lastName': 'Nachname des Kontoinhabers',
    'payto.await.waitForConfirmation': 'Wir warten auf Ihre Genehmigung',
    'payto.bsb.option.label': 'BLZ und Kontonummer',
    'card.fastlane.a11y.logo': 'Fastlane by Paypal-Logo',
    'card.fastlane.a11y.openDialog': 'Lesen Sie mehr über Fastlane',
    'card.fastlane.a11y.closeDialog': 'Dialog schließen',
    'card.fastlane.mobileInputLabel': 'Handynummer',
    'card.fastlane.consentToggle':
        'Speichern Sie Ihre Daten mit Fastlane für schnellere Bezahlvorgänge und mehr',
    'card.fastlane.consentText':
        'Durch die Speicherung Ihrer Daten stimmen Sie zu, Codes per SMS zu erhalten, um Fastlane überall dort zu nutzen, wo es verfügbar ist. Fastlane wird eine SMS an die angegebene Nummer senden. Sie stimmen auch diesen %#Bedingungen%# und der %#Datenschutzerklärung%# zu.',
    'card.fastlane.modal.benefit1.header': 'Automatisches Ausfüllen Ihrer Zahlungen',
    'card.fastlane.modal.benefit1.text':
        'Erhalten Sie Einmalcodes, um die von Ihnen gespeicherten Karten und Adressen zu verwenden.',
    'card.fastlane.modal.benefit2.header': 'Schützen Sie Ihre Daten',
    'card.fastlane.modal.benefit2.text':
        'Ihre Zahlungsinformationen werden verschlüsselt gespeichert und an Ihre Einkaufsorte gesendet.',
    'card.fastlane.modal.benefit3.header': 'Filialübergreifend einsetzbar',
    'card.fastlane.modal.benefit3.text':
        'Beschleunigen Sie den Bezahlvorgang überall, wo Fastlane verfügbar ist.',
    'paybybankpix.redirectBtn.label': 'Weiter zu PIX über OpenBanking',
    'paybybankpix.issuerList.disclaimer':
        'Durch Klicken auf „Weiter“ stimmen Sie der %#Einwilligungserklärung%# zu und werden zu Ihrer Bank weitergeleitet.',
    'paybybankpix.issuerList.selectField.label': 'Bank',
    'paybybankpix.issuerList.introduction.logo.content':
        'Verbinden Sie Ihre bevorzugten PIX-Schlüssel, um über Open Finance zu bezahlen',
    'paybybankpix.await.waitForConfirmation': 'Wir warten auf Ihre Bestätigung...',
    'paybybankpix.await.timeToPay': 'Dieser Vorgang wird ungefähr %{numberOfMin} Minute(n) dauern',
    'paybybankpix.await.withOpenFinance': 'Mit der Leichtigkeit von Open Finance',
    'paybybankpix.await.logoAlt.openFinance': 'Open Finance',
    'paybybankpix.storedPayment.payButton.label': 'Mit Zahlung fortfahren',
    'paybybankpix.storedPayment.additionalLabel': '%{receiver}-Konto über PIX',
    'paybybankpix.await.fetchDetails': 'Details werden abgerufen...',
    'eftpad-canada.input.accountHolderName.label': 'Name des Kontoinhabers',
    'eftpad-canada.input.accountHolderName.error': 'Geben Sie den Namen eines Kontoinhabers ein',
    'eftpad-canada.input.accountNumber.label': 'Kontonummer',
    'eftpad-canada.input.accountNumber.contextualText': 'Zwischen 7 und 12 Stellen',
    'eftpad-canada.input.accountNumber.error':
        'Geben Sie eine Kontonummer ein, die zwischen 7 und 12 Ziffern lang ist',
    'eftpad-canada.input.institutionNumber.label': 'Nummer der Institution',
    'eftpad-canada.input.institutionNumber.contextualText': '3 Stellen',
    'eftpad-canada.input.institutionNumber.error':
        'Geben Sie eine Institutionsnummer ein, die aus 3 Ziffern besteht',
    'eftpad-canada.input.transitNumber.label': 'Transitnummer',
    'eftpad-canada.input.transitNumber.contextualText': '5 Stellen',
    'eftpad-canada.input.transitNumber.error':
        'Geben Sie eine Transitnummer ein, die aus 5 Ziffern besteht',
    'eftpad-canada.settlement-info':
        'Die Gelder werden innerhalb von 1–3 Werktagen von Ihrem Bankkonto abgebucht.',
    'upi.mandate.intro': 'Sie richten eine wiederkehrende UPI-Autopay-Zahlung ein',
    'upi.mandate.max.extraText':
        'Sie genehmigen ein höheres Limit, um zukünftige Planänderungen zu ermöglichen',
    'upi.mandate.upTo': 'bis zu %{amount}',
    'upi.mandate.frequency.monthly': '/Monat',
    'upi.mandate.frequency.weekly': '/Woche',
    'upi.mandate.frequency.adhoc': ' wie dargestellt'
}

export const CHECKOUT_CUSTOMIZATIONS = {
    onError: (error, component) => {
        console.error(error.name, error.message, error.stack, component)
    },
    paymentMethodsConfiguration: {
        klarna: {
            useKlarnaWidget: false
        },
        klarna_account: {
            useKlarnaWidget: false
        },
        klarna_paynow: {
            useKlarnaWidget: false
        }
    },
    translations: {
        'it-CH': adyenIt,
        'en-CH': adyenEn,
        'fr-CH': adyenFr,
        'de-CH': adyenDe,
        'en-DE': adyenEn,
        'de-DE': adyenDe,
        'fr-BE': adyenFr,
        'nl-BE': adyenNl,
        'it-IT': adyenIt,
        'nl-NL': adyenNl,
        'de-AT': adyenDe,
        'fr-FR': adyenFr,
        'en-GB': adyenEn
    }
}
